import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetCollectionManParties, CollectionAging, GetBankRecBank, UpdateCollectionManLatLog, PostPartyCollection, GetPartyWiseCollactionData, GetCollactionData, PartyWiseSchedulePost, GetSchedulePost, PartywiseGetSchedulePost } from '../../Redux/Slice/CollectionMan/CollectionManSlice';
import {
  Search, AlertCircle, ArrowRight, ChevronDown,
  Calendar, DollarSign, IndianRupee, UploadCloud, TrendingUp, MapPin, Route, FileText, CheckCircle2,
  Clock, AlertTriangle, Eye, Send, Briefcase, Sparkles,
  List, Settings, Plus, User, RefreshCw, X, Check, Info, Download
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid,
  AreaChart, Area, Cell
} from 'recharts';
import CollectionAgingTable from './CollectionAgingTable';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select option',
  className = '',
  id = '',
  isFormStyle = false,
  getLabel = null,
  getValue = null,
  disabled = false,
  isMulti = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 50);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const normalizedOptions = [
    { value: '', label: placeholder },
    ...options.map((opt, idx) => {
      if (typeof opt === 'object' && opt !== null) {
        const val = getValue ? getValue(opt) : (opt.value !== undefined ? opt.value : (opt.acid || opt.AcID || opt.AcId || opt.PayStyleID || opt.id || opt.ID || ''));
        const lbl = getLabel ? getLabel(opt) : (opt.label !== undefined ? opt.label : (opt.PartyName || opt.BankName || opt.AcName || opt.Bank || opt.Name || opt.PayStyle || opt.PaymentStyle || ''));
        return { value: val, label: lbl, original: opt };
      }
      return { value: opt, label: opt, original: opt };
    })
  ];

  const filteredOptions = normalizedOptions.filter(opt =>
    String(opt.label).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedValues = isMulti ? (Array.isArray(value) ? value : []) : [value];
  const selectedValuesStr = selectedValues.map(v => String(v ?? ''));
  const selectedOptions = normalizedOptions.filter(opt => selectedValuesStr.includes(String(opt.value)));

  const displayText = isMulti
    ? (selectedOptions.length > 0 ? `${selectedOptions.length} selected` : placeholder)
    : (selectedOptions.length > 0 ? selectedOptions[0].label : placeholder);

  const handleSelect = (val) => {
    if (isMulti) {
      if (val === '') {
        onChange([]);
        setIsOpen(false);
        return;
      }
      const strVal = String(val);
      const currentVals = Array.isArray(value) ? [...value] : [];
      if (currentVals.includes(strVal)) {
        onChange(currentVals.filter(v => v !== strVal));
      } else {
        onChange([...currentVals, strVal]);
      }
    } else {
      onChange(val);
      setIsOpen(false);
    }
  };

  return (
    <div
      className={`searchable-select-container ${className}`}
      ref={containerRef}
      onKeyDown={handleKeyDown}
      style={{ width: '100%' }}
    >
      <button
        type="button"
        id={id}
        className={`searchable-select-trigger ${isFormStyle ? 'form-style' : 'filter-style'} ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
      >
        <span className="trigger-text">{displayText}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {(isMulti ? value && value.length > 0 : value) && !disabled && (
            <span
              className="searchable-select-clear-btn"
              onClick={(e) => {
                e.stopPropagation();
                onChange(isMulti ? [] : '');
              }}
              style={{
                color: '#94a3b8',
                fontSize: '18px',
                lineHeight: '1',
                padding: '0 4px',
                cursor: 'pointer',
                transition: 'color 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 15,
              }}
              onMouseEnter={(e) => { e.target.style.color = '#ef4444'; }}
              onMouseLeave={(e) => { e.target.style.color = '#94a3b8'; }}
              title="Clear selection"
            >
              ×
            </span>
          )}
          <ChevronDown className="trigger-arrow" size={16} />
        </div>
      </button>

      {isOpen && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search-container">
            <Search className="searchable-select-search-icon" size={14} />
            <input
              type="text"
              ref={searchInputRef}
              className="searchable-select-search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="searchable-select-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => {
                const isSelected = isMulti
                  ? (Array.isArray(value) && value.includes(String(opt.value)))
                  : String(opt.value) === String(value);
                return (
                  <div
                    key={idx}
                    className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(opt.value);
                    }}
                    style={isMulti ? { display: 'flex', alignItems: 'center', gap: '8px' } : {}}
                  >
                    {isMulti && opt.value !== '' && (
                      <input type="checkbox" checked={isSelected} readOnly style={{ cursor: 'pointer' }} />
                    )}
                    {opt.label}
                  </div>
                );
              })
            ) : (
              <div className="searchable-select-no-results">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const CollectionMan = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [hasPartyWiseResponse, setHasPartyWiseResponse] = useState(false);
  const { partiesList, banksList, paymentTypesList = [], partyWiseCollectionList = [], collectionList = [], scheduleList = [], agingData = [], agingLoading = false } = useSelector(state => state.CollectionManSlice || {});

  // Schedule Visit State declared at top to avoid TDZ reference errors
  const [visitFilter, setVisitFilter] = useState('All'); // All, Completed, Scheduled, Cancelled
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showBucketModal, setShowBucketModal] = useState(false);
  const [bucketInterval, setBucketInterval] = useState(15);
  const [scheduleViewMode, setScheduleViewMode] = useState('calendar'); // 'list' or 'calendar'
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(new Date().getDate());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());


  const getFieldValue = (item, keys) => {
    if (!item) return '';
    for (let key of keys) {
      if (item[key] !== undefined && item[key] !== null) {
        return item[key];
      }
    }
    return '';
  };

  const derivedVisitSchedules = Array.isArray(scheduleList) && scheduleList.length > 0
    ? scheduleList.map((item, index) => {
      const partyName = getFieldValue(item, ['PartyName', 'AcName', 'partyName', 'Party', 'party']) || '';
      let nextDate = getFieldValue(item, ['NxtVisitDate', 'Date', 'NextVisitDate', 'date', 'currentDate']) || '';
      if (nextDate && nextDate.includes('T')) {
        nextDate = nextDate.split('T')[0];
      }

      let dateFormatted = '';
      let yearVal = new Date().getFullYear();
      let monthVal = new Date().getMonth();
      let dayVal = new Date().getDate();
      if (nextDate) {
        const parsedDate = new Date(nextDate);
        if (!isNaN(parsedDate.getTime())) {
          yearVal = parsedDate.getFullYear();
          monthVal = parsedDate.getMonth(); // 0-indexed
          dayVal = parsedDate.getDate();
          const day = dayVal.toString().padStart(2, '0');
          const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
          const month = months[monthVal];
          dateFormatted = `${day} ${month}`;
        }
      }

      let visitTime = getFieldValue(item, ['NxtVisitTime', 'Time', 'VisitTime', 'time']) || '';
      if (visitTime && visitTime.includes('T')) {
        const timePart = visitTime.split('T')[1];
        if (timePart) {
          visitTime = timePart.substring(0, 5);
        }
      }

      const priority = getFieldValue(item, ['Priority', 'Preority', 'priority', 'preority']) || 'NORMAL';
      const remark = getFieldValue(item, ['Agenda', 'Remark', 'remark', 'Remarks', 'Remarks1', 'agenda']) || '';
      const agent = getFieldValue(item, ['Agent', 'agent', 'UserName', 'username', 'User', 'user']) || '';
      const area = getFieldValue(item, ['Area', 'area', 'City', 'city', 'CityName', 'cityName']) || '';

      return {
        id: getFieldValue(item, ['id', 'ID', 'Id']) || index + 1,
        date: dateFormatted,
        year: yearVal,
        month: monthVal,
        day: dayVal,
        name: partyName,
        priority: priority.toUpperCase(),
        status: (getFieldValue(item, ['Status', 'status']) || priority || 'SCHEDULED').toUpperCase(),
        time: visitTime,
        agent: agent,
        area: area,
        outcome: remark
      };
    })
    : [];

  const derivedCalendarEvents = {};
  derivedVisitSchedules.forEach((item) => {
    if (item.year === calendarYear && item.month === calendarMonth) {
      const dayNum = item.day;
      if (!derivedCalendarEvents[dayNum]) {
        derivedCalendarEvents[dayNum] = [];
      }
      derivedCalendarEvents[dayNum].push(item);
    }
  });

  // SEO best practices - page titles
  useEffect(() => {
    document.title = "Collection Man - Collection ERP";
  }, []);

  // Main navigation tab
  const [activeSubTab, setActiveSubTab] = useState('daily'); // 'daily', 'aging', 'grey', 'history', 'schedule', etc.

  // Redirect to daily tab if response is reset or not received
  // useEffect removed for grey balance visibility

  // Toast alerts state (disabled completely)
  const addToast = () => { };

  // Dropdown filter states (Market, Party, Prod Group)
  const [marketFilter, setMarketFilter] = useState('');
  const [partyFilter, setPartyFilter] = useState('');
  const [prodGroupFilter, setProdGroupFilter] = useState([]);

  // ----------------------------------------------------
  // DAILY COLLECTION ENTRY STATE & DUMMY DATA
  // ----------------------------------------------------
  const [paymentType, setPaymentType] = useState(''); // Dynamic Payment Type Selection
  const [paymentRefNo, setPaymentRefNo] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentBank, setPaymentBank] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRemark, setPaymentRemark] = useState('');
  const [paymentFile, setPaymentFile] = useState(null);
  const [ledgerSearch, setLedgerSearch] = useState('');


  // Route Management State
  const [routeSequence, setRouteSequence] = useState([]);
  const [routeSearch, setRouteSearch] = useState('');
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  // Recovery Re-Schedule Form
  const [reschedDate, setReschedDate] = useState('');
  const [reschedTime, setReschedTime] = useState('');
  const [reschedPriority, setReschedPriority] = useState('Normal');
  const [reschedAgenda, setReschedAgenda] = useState('');

  const handleAddToRoute = (party) => {
    const pId = party.acid || party.AcID || party.id;
    if (!routeSequence.find(p => (p.acid || p.AcID || p.id) === pId)) {
      setRouteSequence([...routeSequence, party]);
    }
  };

  const handleRemoveFromRoute = (pId) => {
    setRouteSequence(routeSequence.filter(p => (p.acid || p.AcID || p.id) !== pId));
  };

  const handleDragStartRoute = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnterRoute = (e, index) => {
    e.preventDefault();
    if (draggedItemIndex === null) return;
    const draggedOverItemIndex = index;
    if (draggedItemIndex === draggedOverItemIndex) return;

    let items = [...routeSequence];
    const draggedItem = items[draggedItemIndex];
    items.splice(draggedItemIndex, 1);
    items.splice(draggedOverItemIndex, 0, draggedItem);
    
    setDraggedItemIndex(draggedOverItemIndex);
    setRouteSequence(items);
  };

  const handleDragEndRoute = () => {
    setDraggedItemIndex(null);
  };

  const handleDragOverRoute = (e) => {
    e.preventDefault();
  };

  const handleOpenInMap = () => {
    if (routeSequence.length === 0) {
      alert("Please add at least one party to the route.");
      return;
    }
    
    const validPoints = routeSequence.filter(p => (p.Latitude || p.Lat || p.lat) && (p.Longitude || p.Lng || p.lng));
    if (validPoints.length === 0) {
      alert("None of the selected parties have GPS coordinates set.");
      return;
    }

    const origin = validPoints[0];
    const destination = validPoints[validPoints.length - 1];
    const waypoints = validPoints.slice(1, -1);

    const originStr = `${origin.Latitude || origin.Lat || origin.lat},${origin.Longitude || origin.Lng || origin.lng}`;
    const destStr = `${destination.Latitude || destination.Lat || destination.lat},${destination.Longitude || destination.Lng || destination.lng}`;
    const waypointsStr = waypoints.map(wp => `${wp.Latitude || wp.Lat || wp.lat},${wp.Longitude || wp.Lng || wp.lng}`).join('|');

    let url = `https://www.google.com/maps/dir/?api=1&origin=${originStr}&destination=${destStr}`;
    if (waypointsStr) {
      url += `&waypoints=${waypointsStr}`;
    }
    window.open(url, '_blank');
  };

  const handleDownloadRoutePDF = () => {
    if (routeSequence.length === 0) {
      alert("Please add at least one party to the route before downloading.");
      return;
    }
    
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Collector Route Plan", 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    const dateStr = new Date().toLocaleDateString('en-IN');
    doc.text(`Generated on: ${dateStr} | Total Stops: ${routeSequence.length}`, 14, 28);
    
    const tableData = routeSequence.map((party, index) => [
      index + 1,
      party.PartyName || 'Unknown',
      party.CityName || 'Unknown City',
      typeof formatAsINR1 === 'function' ? formatAsINR1(party.Bal || party.TotalOs || 0) : `Rs. ${(party.Bal || party.TotalOs || 0).toLocaleString('en-IN')}`,
      party.Latitude || party.Lat || party.lat ? `${Number(party.Latitude || party.Lat || party.lat).toFixed(4)}, ${Number(party.Longitude || party.Lng || party.lng).toFixed(4)}` : 'Not Set'
    ]);
    
    autoTable(doc, {
      startY: 35,
      head: [['#', 'Party Name', 'City/Area', 'Balance', 'GPS Coordinates']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [14, 165, 233] },
      styles: { fontSize: 9 }
    });

    const validPoints = routeSequence.filter(p => (p.Latitude || p.Lat || p.lat) && (p.Longitude || p.Lng || p.lng));
    if (validPoints.length > 0) {
      const origin = validPoints[0];
      const destination = validPoints[validPoints.length - 1];
      const waypoints = validPoints.slice(1, -1);
      const originStr = `${origin.Latitude || origin.Lat || origin.lat},${origin.Longitude || origin.Lng || origin.lng}`;
      const destStr = `${destination.Latitude || destination.Lat || destination.lat},${destination.Longitude || destination.Lng || destination.lng}`;
      const waypointsStr = waypoints.map(wp => `${wp.Latitude || wp.Lat || wp.lat},${wp.Longitude || wp.Lng || wp.lng}`).join('|');
      let url = `https://www.google.com/maps/dir/?api=1&origin=${originStr}&destination=${destStr}`;
      if (waypointsStr) {
        url += `&waypoints=${waypointsStr}`;
      }
      
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 40;
      doc.setFontSize(12);
      doc.setTextColor(14, 165, 233);
      doc.textWithLink("Click here to Open Route in Google Maps", 14, finalY + 15, { url: url });
    }

    doc.save("Collector_Route_Plan.pdf");
  };

  const handleSavePayment = (e) => {
    e.preventDefault();

    // 1. Find Selected Party ID from partyFilter
    const selectedPartyObj = partiesList && partiesList.find(p => p.PartyName === partyFilter);
    const partyId = selectedPartyObj ? (selectedPartyObj.AcID || selectedPartyObj.acid || selectedPartyObj.AcId || selectedPartyObj.id || selectedPartyObj.ID) : selectedPartyId;

    if (!partyFilter || !partyId) {
      alert("Please select a Party Name from the top filter first!");
      return;
    }

    // 2. Find Selected Bank ID from banksList
    const selectedBankObj = banksList && banksList.find(b => (b.BankName || b.AcName || b.Bank || b.Name) === paymentBank);
    const bankId = selectedBankObj ? (selectedBankObj.AcID || selectedBankObj.AcId || selectedBankObj.acid || selectedBankObj.BankID || selectedBankObj.BankId || selectedBankObj.id || selectedBankObj.ID) : '';

    if (!paymentBank || !bankId) {
      alert("Please select a valid Bank Name!");
      return;
    }

    // 3. Validation for Required fields
    if (!paymentDate) {
      alert("Please select a Voucher / Cheque Date!");
      return;
    }
    if (!paymentRefNo) {
      alert("Please enter a Cheque / Ref No!");
      return;
    }
    if (!paymentType) {
      alert("Please select a Payment Type!");
      return;
    }
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert("Please specify a valid payment amount!");
      return;
    }

    // Remark and Img are optional
    const submitCollection = (base64Data = null, fileName = null) => {
      const payload = {
        PartyID: partyId,
        ChequeNo: paymentRefNo,
        ChequeDate: paymentDate,
        BankId: bankId,
        PaymentStyId: paymentType, // Holds PayStyleID from dropdown
        Amount: paymentAmount,
        Remark: paymentRemark || '0',
        Img: base64Data,
        ImgName: fileName
      };

      dispatch(PostPartyCollection(payload))
        .then((res) => {
          if (res.error) {
            alert(`Error saving payment: ${res.payload || 'Failed to save transaction.'}`);
          } else {
            alert("Payment Entry saved successfully!");



            // Reset Form
            setPaymentRefNo('');
            setPaymentDate('');
            setPaymentBank('');
            setPaymentType('');
            setPaymentAmount('');
            setPaymentRemark('');
            setPaymentFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
        })
        .catch((err) => {
          alert(`Error: ${err.message || 'Network error'}`);
        });
    };

    if (paymentFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        submitCollection(reader.result, paymentFile.name);
      };
      reader.onerror = () => {
        alert("Failed to read the uploaded payment proof file.");
      };
      reader.readAsDataURL(paymentFile);
    } else {
      submitCollection(null, null);
    }
  };

  const handleConfirmProtocol = (e) => {
    e.preventDefault();

    // 1. Find Selected Party ID from partyFilter
    const selectedPartyObj = partiesList && partiesList.find(p => p.PartyName === partyFilter);
    const partyId = selectedPartyObj ? (selectedPartyObj.AcID || selectedPartyObj.acid || selectedPartyObj.AcId || selectedPartyObj.id || selectedPartyObj.ID) : null;

    if (!partyFilter || !partyId) {
      alert("Please select a Party Name from the top filter first!");
      return;
    }

    if (!reschedDate) {
      alert("Please select a recovery visit date!");
      return;
    }

    if (!reschedTime) {
      alert("Please select a visit time!");
      return;
    }

    if (!reschedAgenda || !reschedAgenda.trim()) {
      alert("Please enter Intelligence Agenda!");
      return;
    }

    const payload = {
      PartyID: partyId,
      Date: reschedDate,
      Time: reschedTime || '10:00 AM',
      Preority: (reschedPriority || 'Normal').toUpperCase(),
      Remark: (reschedAgenda || '').trim().toUpperCase()
    };

    dispatch(PartyWiseSchedulePost(payload))
      .then((res) => {
        if (res.error) {
          alert(`Error saving schedule: ${res.payload || 'Failed to save schedule.'}`);
        } else {
          alert("Recovery Re-Schedule saved successfully!");
          dispatch(PartywiseGetSchedulePost({ PartyID: partyId }));
          // Reset form fields
          setReschedDate('');
          setReschedTime('');
          setReschedPriority('Normal');
          setReschedAgenda('');
        }
      })
      .catch((err) => {
        alert(`Error: ${err.message || 'Network error'}`);
      });
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setPaymentFile(e.dataTransfer.files[0]);
      addToast(`Selected file: ${e.dataTransfer.files[0].name}`, "success");
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentFile(e.target.files[0]);
      addToast(`Selected file: ${e.target.files[0].name}`, "success");
    }
  };

  // ----------------------------------------------------
  // AGING ANALYSIS STATE & DATA
  // ----------------------------------------------------


  const [agingGroupType] = useState(''); // Empty string for no default grouping

  const dynamicAgingChartData = useMemo(() => {
    if (!agingData || agingData.length === 0) return [];

    // Standard non-bucket columns in lowercase
    const excludeKeys = ['acid', 'collectionid', 'groupmstid', 'productiongroupid', 'id', 'srnumber', 'partyid', 'fyear', 'companyid', 'acname', 'collectionname', 'groupmstname', 'productiongroupname', 'totalos', 'onac', 'net', 'balance'];

    // Identify bucket columns from the first row's keys
    const bucketKeys = Object.keys(agingData[0]).filter(key => {
      const lowerKey = key.toLowerCase();
      if (excludeKeys.includes(lowerKey)) return false;
      if (lowerKey.includes('total') || lowerKey.includes('bal')) return false;

      // We consider it a bucket if its value is numeric and not in our exclude list
      const val = agingData[0][key];
      return typeof val === 'number' || (typeof val === 'string' && val.trim() !== '' && !isNaN(parseFloat(val.replace(/,/g, ''))));
    });

    const chartData = [];
    const colors = ['#3b82f6', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];

    bucketKeys.forEach((bucketKey, index) => {
      let total = 0;
      agingData.forEach(row => {
        const val = row[bucketKey];
        if (val != null && val !== '') {
          const numericVal = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val;
          if (!isNaN(numericVal)) {
            total += numericVal;
          }
        }
      });

      chartData.push({
        name: bucketKey.toLowerCase().includes('day') ? bucketKey : `${bucketKey} Days`,
        value: total,
        fill: colors[index % colors.length]
      });
    });

    return chartData;
  }, [agingData]);

  const topCriticalUnits = useMemo(() => {
    if (!agingData || agingData.length === 0) return [];

    const excludeKeys = ['acid', 'collectionid', 'groupmstid', 'productiongroupid', 'id', 'srnumber', 'partyid', 'fyear', 'companyid', 'acname', 'collectionname', 'groupmstname', 'productiongroupname', 'totalos', 'onac', 'net', 'balance'];

    const bucketKeys = Object.keys(agingData[0]).filter(key => {
      const lowerKey = key.toLowerCase();
      if (excludeKeys.includes(lowerKey)) return false;
      if (lowerKey.includes('total') || lowerKey.includes('bal')) return false;
      const val = agingData[0][key];
      return typeof val === 'number' || (typeof val === 'string' && val.trim() !== '' && !isNaN(parseFloat(val.replace(/,/g, ''))));
    });

    // The last bucket key is typically the oldest/highest risk bucket
    const oldestBucketKey = bucketKeys.length > 0 ? bucketKeys[bucketKeys.length - 1] : null;

    const getRowTotal = (row) => {
      if (oldestBucketKey && row[oldestBucketKey] != null && row[oldestBucketKey] !== '') {
        const val = parseFloat(String(row[oldestBucketKey]).replace(/,/g, ''));
        if (!isNaN(val)) return val;
      }
      // Fallback to total if no buckets are found
      const possibleKeys = ['net', 'totalos', 'total'];
      for (let pkey of possibleKeys) {
        const actualKey = Object.keys(row).find(k => k.toLowerCase() === pkey);
        if (actualKey && row[actualKey] != null && row[actualKey] !== '') {
          const val = parseFloat(String(row[actualKey]).replace(/,/g, ''));
          if (!isNaN(val)) return val;
        }
      }
      return 0;
    };

    let grandTotal = 0;
    const enriched = [];
    agingData.forEach(row => {
      const isGrandTotal = Object.values(row).some(val =>
        typeof val === 'string' && val.toLowerCase().includes('grand total')
      );
      if (!isGrandTotal) {
        const total = getRowTotal(row);
        grandTotal += total;
        enriched.push({ ...row, calculatedTotal: total });
      }
    });

    if (grandTotal === 0) grandTotal = 1;

    enriched.sort((a, b) => b.calculatedTotal - a.calculatedTotal);

    return enriched.filter(row => row.calculatedTotal > 0).map(row => {
      const nameKey = Object.keys(row).find(k => k.toLowerCase() === 'collectionname') || Object.keys(row).find(k => k.toLowerCase() === 'acname');
      const groupKey = Object.keys(row).find(k => k.toLowerCase() === 'productiongroupname') || Object.keys(row).find(k => k.toLowerCase() === 'groupmstname');

      return {
        name: nameKey ? row[nameKey] : 'Unknown',
        group: groupKey ? row[groupKey] : 'General',
        amount: row.calculatedTotal,
        percentage: ((row.calculatedTotal / grandTotal) * 100).toFixed(2),
        bucketName: oldestBucketKey ? `${oldestBucketKey} Days` : 'Highest'
      };
    });
  }, [agingData]);

  const agingKpis = useMemo(() => {
    if (!agingData || agingData.length === 0) return {
      total: 0, highRisk: 0, intermediate: 0, fresh: 0,
      totalCount: 0, highRiskPct: 0, intermediatePct: 0, freshPct: 0,
      highRiskLabel: 'High Risk', freshLabel: 'Fresh Dues'
    };

    const excludeKeys = ['acid', 'collectionid', 'groupmstid', 'productiongroupid', 'id', 'srnumber', 'partyid', 'fyear', 'companyid', 'acname', 'collectionname', 'groupmstname', 'productiongroupname', 'totalos', 'onac', 'net', 'balance'];
    const bucketKeys = Object.keys(agingData[0]).filter(key => {
      const lowerKey = key.toLowerCase();
      if (excludeKeys.includes(lowerKey) || lowerKey.includes('total') || lowerKey.includes('bal')) return false;
      const val = agingData[0][key];
      return typeof val === 'number' || (typeof val === 'string' && val.trim() !== '' && !isNaN(parseFloat(val.replace(/,/g, ''))));
    });

    // Safely sort bucket keys so '0-15' is first and '60+' is last
    bucketKeys.sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    });

    let total = 0;
    let highRisk = 0;
    let fresh = 0;
    let intermediate = 0;

    const oldestBucket = bucketKeys.length > 0 ? bucketKeys[bucketKeys.length - 1] : null;
    const newestBucket = bucketKeys.length > 0 ? bucketKeys[0] : null;
    const intermediateBuckets = bucketKeys.slice(1, -1);

    const parseVal = (val) => {
      if (val == null || val === '') return 0;
      const num = parseFloat(String(val).replace(/,/g, ''));
      return isNaN(num) ? 0 : num;
    };

    let validRecordsCount = 0;
    const grandTotalRow = agingData.find(row =>
      Object.values(row).some(val =>
        typeof val === 'string' && val.toLowerCase().includes('grand total')
      )
    );

    agingData.forEach(row => {
      const isGrandTotal = Object.values(row).some(val =>
        typeof val === 'string' && val.toLowerCase().includes('grand total')
      );
      if (!isGrandTotal) {
        validRecordsCount++;
      }
    });

    if (grandTotalRow) {
      const netKey = Object.keys(grandTotalRow).find(k => k.toLowerCase() === 'net');
      const totalOsKey = Object.keys(grandTotalRow).find(k => k.toLowerCase() === 'totalos' || k.toLowerCase() === 'total');

      if (netKey && grandTotalRow[netKey] != null) {
        total = parseVal(grandTotalRow[netKey]);
      } else if (totalOsKey && grandTotalRow[totalOsKey] != null) {
        total = parseVal(grandTotalRow[totalOsKey]);
      }

      if (oldestBucket) highRisk = parseVal(grandTotalRow[oldestBucket]);
      if (newestBucket) fresh = parseVal(grandTotalRow[newestBucket]);
      intermediateBuckets.forEach(bk => intermediate += parseVal(grandTotalRow[bk]));

      const pctBase = (highRisk + intermediate + fresh) || 1;

      const formatTooltipCurrency = (num) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(num);

      const highRiskTooltip = oldestBucket ? `Calculated from bucket: ${oldestBucket} Days\nAmount: ${formatTooltipCurrency(highRisk)}` : '';
      const freshTooltip = newestBucket ? `Calculated from bucket: ${newestBucket} Days\nAmount: ${formatTooltipCurrency(fresh)}` : '';
      const intermediateBreakdown = intermediateBuckets.map(bk => `${bk} Days: ${formatTooltipCurrency(parseVal(grandTotalRow[bk]))}`).join('\n+ ');
      const intermediateTooltip = intermediateBuckets.length > 0 ? `Sum of intermediate buckets:\n${intermediateBreakdown}\n= ${formatTooltipCurrency(intermediate)}` : 'No intermediate buckets';

      const totalTooltip = `Based on Grand Total row.\nHigh Risk + Intermediate + Fresh\n= ${formatTooltipCurrency(highRisk + intermediate + fresh)}`;

      return {
        total,
        highRisk,
        intermediate,
        fresh,
        totalCount: validRecordsCount,
        highRiskPct: ((highRisk / (pctBase || 1)) * 100).toFixed(2),
        intermediatePct: ((intermediate / (pctBase || 1)) * 100).toFixed(2),
        freshPct: ((fresh / (pctBase || 1)) * 100).toFixed(2),
        highRiskLabel: oldestBucket ? `High Risk (Above ${oldestBucket.replace('+', '')} Days)` : 'High Risk',
        freshLabel: newestBucket ? `Fresh Dues (${newestBucket} Days)` : 'Fresh Dues',
        totalTooltip,
        highRiskTooltip,
        intermediateTooltip,
        freshTooltip
      };
    } else {
      let fallbackTotalOS = 0;
      agingData.forEach(row => {
        let rowTotal = 0;
        bucketKeys.forEach(bk => rowTotal += parseVal(row[bk]));
        fallbackTotalOS += rowTotal;
        if (oldestBucket) highRisk += parseVal(row[oldestBucket]);
        if (newestBucket) fresh += parseVal(row[newestBucket]);
        intermediateBuckets.forEach(bk => intermediate += parseVal(row[bk]));
      });

      const netKey = Object.keys(agingData[0]).find(k => k.toLowerCase() === 'net');
      if (netKey) {
        total = agingData.reduce((acc, row) => acc + parseVal(row[netKey]), 0);
      } else {
        total = fallbackTotalOS;
      }

      const safeTotal = fallbackTotalOS || 1;

      const formatTooltipCurrency = (num) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(num);

      const highRiskTooltip = oldestBucket ? `Calculated from bucket: ${oldestBucket} Days\nAmount: ${formatTooltipCurrency(highRisk)}` : '';
      const freshTooltip = newestBucket ? `Calculated from bucket: ${newestBucket} Days\nAmount: ${formatTooltipCurrency(fresh)}` : '';
      const intermediateTooltip = intermediateBuckets.length > 0 ? `Sum of intermediate buckets:\nAmount: ${formatTooltipCurrency(intermediate)}` : 'No intermediate buckets';
      const totalTooltip = `Sum of all valid rows.\nHigh Risk + Intermediate + Fresh\n= ${formatTooltipCurrency(highRisk + intermediate + fresh)}`;

      return {
        total,
        highRisk,
        intermediate,
        fresh,
        totalCount: validRecordsCount,
        highRiskPct: ((highRisk / safeTotal) * 100).toFixed(2),
        intermediatePct: ((intermediate / safeTotal) * 100).toFixed(2),
        freshPct: ((fresh / safeTotal) * 100).toFixed(2),
        highRiskLabel: oldestBucket ? `High Risk (Above ${oldestBucket.replace('+', '')} Days)` : 'High Risk',
        freshLabel: newestBucket ? `Fresh Dues (${newestBucket} Days)` : 'Fresh Dues',
        totalTooltip,
        highRiskTooltip,
        intermediateTooltip,
        freshTooltip
      };
    }
  }, [agingData]);

  // Form states to add schedule
  const [newVisitName, setNewVisitName] = useState('');
  const [newVisitDate, setNewVisitDate] = useState(new Date().toLocaleDateString('sv-SE')); // ISO date format in local timezone (YYYY-MM-DD)
  const [newVisitTime, setNewVisitTime] = useState('10:00');
  const [newVisitPriority, setNewVisitPriority] = useState('Normal');
  const [newVisitAgenda, setNewVisitAgenda] = useState('');

  const handleAddNewVisit = (e) => {
    e.preventDefault();
    if (!newVisitName) {
      alert("Please select customer party name");
      return;
    }

    const selectedPartyObj = partiesList && partiesList.find(p => p.PartyName === newVisitName);
    const partyId = selectedPartyObj ? (selectedPartyObj.AcID || selectedPartyObj.acid || selectedPartyObj.AcId || selectedPartyObj.id || selectedPartyObj.ID) : null;

    if (!partyId) {
      alert("Please select a valid customer!");
      return;
    }

    if (!newVisitDate) {
      alert("Please select visit date!");
      return;
    }

    if (!newVisitTime) {
      alert("Please select visit time!");
      return;
    }

    if (!newVisitAgenda || !newVisitAgenda.trim()) {
      alert("Please enter Intelligence Agenda!");
      return;
    }

    const payload = {
      PartyID: partyId,
      Date: newVisitDate,
      Time: newVisitTime || '10:00 AM',
      Preority: (newVisitPriority || 'Normal').toUpperCase(),
      Remark: (newVisitAgenda || '').trim().toUpperCase()
    };

    dispatch(PartyWiseSchedulePost(payload))
      .then((res) => {
        if (res.error) {
          alert(`Error saving schedule: ${res.payload || 'Failed to save schedule.'}`);
        } else {
          alert("Schedule saved successfully!");
          dispatch(GetSchedulePost());
          // Reset Form
          setNewVisitName('');
          setNewVisitDate(new Date().toLocaleDateString('sv-SE'));
          setNewVisitTime('10:00');
          setNewVisitPriority('Normal');
          setNewVisitAgenda('');
          setShowScheduleModal(false);
        }
      })
      .catch((err) => {
        alert(`Error: ${err.message || 'Network error'}`);
      });
  };

  // Helper formats
  const formatAsINR = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(val) || 0);
  };

  const formatAsINR1 = (val) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(val) || 0);
  };
  const uniqueMarkets = Array.isArray(partiesList)
    ? Array.from(new Set(partiesList.map(p => p.MarketName || p.Market || p.GroupMstName || p.GroupName || p.CityName || p.AreaName).filter(Boolean)))
    : [];

  const filteredPartiesForDropdown = Array.isArray(partiesList)
    ? (marketFilter ? partiesList.filter(p => (p.MarketName || p.Market || p.GroupMstName || p.GroupName || p.CityName || p.AreaName) === marketFilter) : partiesList)
    : [];

  const uniqueProductionGroups = Array.isArray(partiesList)
    ? Array.from(new Set(
      partiesList
        .filter(p => {
          if (marketFilter && (p.MarketName || p.Market || p.GroupMstName || p.GroupName || p.CityName || p.AreaName) !== marketFilter) return false;
          if (partyFilter && p.PartyName !== partyFilter) return false;
          return true;
        })
        .map(p => p.ProductionGroupMstName)
        .filter(Boolean)
    ))
    : [];

  const applyFilters = () => {
    if (!Array.isArray(partiesList) || partiesList.length === 0) return;

    let filtered = partiesList;
    if (marketFilter) {
      filtered = filtered.filter(p => (p.MarketName || p.Market || p.GroupMstName || p.GroupName || p.CityName || p.AreaName) === marketFilter);
    }
    if (partyFilter) {
      filtered = filtered.filter(p => p.PartyName === partyFilter);
    }
    if (prodGroupFilter && prodGroupFilter.length > 0) {
      filtered = filtered.filter(p => prodGroupFilter.includes(p.ProductionGroupMstName));
    }
    if (partyFilter) {
      dispatch(GetBankRecBank());
      const selectedPartyObj = partiesList.find(p => p.PartyName === partyFilter);
      if (selectedPartyObj) {
        const partyId = selectedPartyObj.AcID || selectedPartyObj.acid || selectedPartyObj.AcId || selectedPartyObj.id || selectedPartyObj.ID;
        dispatch(GetPartyWiseCollactionData({ PartyID: partyId })).then(() => {
          setHasPartyWiseResponse(true);
        });
        dispatch(PartywiseGetSchedulePost({ PartyID: partyId }));
        dispatch(CollectionAging({ PartyID: partyId }));
      }
    } else {
      dispatch(GetCollactionData()).then(() => {
        setHasPartyWiseResponse(false);
      });
      dispatch(GetSchedulePost());
      dispatch(CollectionAging());
    }
    const fetchedParties = filtered.map((item, index) => {
      const name = item.PartyName || item.AcName || `Party ${index + 1}`;
      const id = item.acid || item.AcId || index + 1;
      const area = item.CityName || item.AreaName || 'Ahmedabad';

      let lat = parseFloat(item.Latitude || item.Lat || item.latitude || item.lat);
      let lng = parseFloat(item.Longitude || item.Lng || item.longitude || item.lng);

      if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
        lat = null;
        lng = null;
      }

      return {
        id,
        name,
        area,
        lat,
        lng,
        status: item.Status || 'Active',
        selected: index === 0
      };
    });

    setParties(fetchedParties);
    if (fetchedParties.length > 0) {
      setSelectedPartyId(fetchedParties[0].id);
    }
  };

  // -----------------------------------------------------
  // GPS LAT/LOG TRACKING STATE & DATA
  // -----------------------------------------------------
  const [latlogSubTab, setLatlogSubTab] = useState('single');
  const [selectedPartyId, setSelectedPartyId] = useState(1);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [latlogSearch, setLatlogSearch] = useState('');

  const [parties, setParties] = useState([
    { id: 1, name: 'Party 1 - Ahmedabad Textile', area: 'Navrangpura', lat: 23.0250, lng: 72.5620, status: 'Active', selected: true },
    { id: 2, name: 'Party 2 - Reliance Petrol', area: 'Ashram Road', lat: 23.0380, lng: 72.5730, status: 'Active', selected: true },
    { id: 3, name: 'Party 3 - Gujarat Steels', area: 'Satellite', lat: 23.0300, lng: 72.5180, status: 'Pending', selected: false },
    { id: 4, name: 'Party 4 - Bharat Electronics', area: 'Maninagar', lat: 22.9980, lng: 72.6020, status: 'Active', selected: false },
    { id: 5, name: 'Party 5 - Tata Motors Service', area: 'SG Highway', lat: 23.0520, lng: 72.5010, status: 'Active', selected: false },
    { id: 6, name: 'ABC Enterprises', area: 'Navrangpura', lat: 23.0330, lng: 72.5550, status: 'Active', selected: false },
    { id: 7, name: 'Global Merchants', area: 'CG Road', lat: 23.0280, lng: 72.5590, status: 'Active', selected: false },
    { id: 8, name: 'LMN Pvt Ltd', area: 'Ashram Road', lat: 23.0410, lng: 72.5690, status: 'Active', selected: false },
  ]);


  useEffect(() => {
    dispatch(GetCollectionManParties());
    dispatch(GetCollactionData());
    dispatch(GetSchedulePost());
    dispatch(CollectionAging());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(partiesList) && partiesList.length > 0) {
      const fetchedParties = partiesList.map((item, index) => {
        const name = item.PartyName || item.AcName || item.partyName || item.ClientName || `Party ${index + 1}`;
        const id = item.acid || item.AcId || item.AcID || item.id || item.ID || index + 1;
        const area = item.CityName || item.AreaName || item.Area || 'Ahmedabad';

        let lat = parseFloat(item.Latitude || item.Lat || item.latitude || item.lat);
        let lng = parseFloat(item.Longitude || item.Lng || item.longitude || item.lng);

        if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
          lat = null;
          lng = null;
        }

        return {
          id,
          name,
          area,
          lat,
          lng,
          status: item.Status || 'Active',
          selected: index === 0
        };
      });

      setParties(fetchedParties);
      setSelectedPartyId(fetchedParties[0].id);
      addToast("Collection Man parties loaded successfully!", "success");
    }
  }, [partiesList]);

  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const polylineRef = useRef(null);

  const initLeafletMap = () => {
    if (!window.L) return;
    const container = document.getElementById('leaflet-map-container');
    if (!container) return;

    // If map already exists, clear existing markers and polylines first
    if (mapInstance.current) {
      // Clear markers
      Object.values(markersRef.current).forEach(m => m.remove());
      markersRef.current = {};
      if (polylineRef.current) {
        polylineRef.current.remove();
        polylineRef.current = null;
      }
    } else {
      // Initialize map
      mapInstance.current = window.L.map('leaflet-map-container').setView([23.0225, 72.5714], 13);

      // Add standard street map tiles
      window.L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
        attribution: '&copy; Google Maps'
      }).addTo(mapInstance.current);
    }

    const map = mapInstance.current;

    // Draw markers based on current mode and selections
    const activeParties = latlogSubTab === 'single'
      ? parties.filter(p => p.id === selectedPartyId)
      : parties.filter(p => p.selected);

    const latlngs = [];

    activeParties.forEach(p => {
      if (p.lat && p.lng) {
        latlngs.push([p.lat, p.lng]);

        // Custom pulsing map dot indicator
        const customIcon = window.L.divIcon({
          className: 'custom-leaflet-icon',
          html: `<div style="position: relative;">
                   <div class="pulse-ring" style="position: absolute; top: -10px; left: -10px; width: 30px; height: 30px; border-radius: 50%; background: rgba(14, 165, 233, 0.35); animation: pulseMarker 1.8s infinite;"></div>
                   <div class="inner-dot" style="position: absolute; top: 0; left: 0; width: 10px; height: 10px; border-radius: 50%; background: #0ea5e9; border: 2px solid #ffffff; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>
                 </div>`,
          iconSize: [10, 10],
          iconAnchor: [5, 5]
        });

        const marker = window.L.marker([p.lat, p.lng], { icon: customIcon })
          .addTo(map)
          .bindPopup(`<strong>${p.name}</strong><br/>Area: ${p.area}<br/>Lat: ${p.lat.toFixed(6)}, Lng: ${p.lng.toFixed(6)}`);

        markersRef.current[p.id] = marker;
      }
    });

    // Draw beat route line connecting multiple points
    if (latlogSubTab === 'multi' && latlngs.length > 1) {
      polylineRef.current = window.L.polyline(latlngs, {
        color: '#0ea5e9',
        weight: 3,
        dashArray: '8, 6',
        opacity: 0.8
      }).addTo(map);
    }

    // Set map focus boundaries
    if (latlogSubTab === 'single' && latlngs.length > 0) {
      map.setView(latlngs[0], 14);
      markersRef.current[selectedPartyId]?.openPopup();
    } else if (latlogSubTab === 'multi' && latlngs.length > 0) {
      const bounds = window.L.latLngBounds(latlngs);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  };

  useEffect(() => {
    if (activeSubTab === 'latlog') {
      // Dynamic CSS Load
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }

      // Dynamic script library load
      if (!window.L) {
        const script = document.createElement('script');
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.async = true;
        script.onload = () => {
          initLeafletMap();
        };
        document.body.appendChild(script);
      } else {
        const timer = setTimeout(initLeafletMap, 50);
        return () => clearTimeout(timer);
      }
    }

    return () => {
      if (activeSubTab !== 'latlog' && mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [activeSubTab, latlogSubTab, selectedPartyId, parties]);

  const [tempGPS, setTempGPS] = useState(null); // { lat, lng }

  const handleUpdateGPS = (partyId) => {
    if (!navigator.geolocation) {
      addToast("Geolocation is not supported by your browser", "error");
      return;
    }
    setGpsLoading(true);
    addToast("Requesting browser GPS permission...", "success");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const fetchedLat = parseFloat(latitude.toFixed(6));
        const fetchedLng = parseFloat(longitude.toFixed(6));
        setTempGPS({ lat: fetchedLat, lng: fetchedLng });
        setGpsLoading(false);
        addToast("GPS coordinates fetched successfully! Click 'Update' below to save.", "success");
      },
      (error) => {
        setGpsLoading(false);
        console.warn("GPS error, using fallback simulation: ", error.message);

        const baseParty = parties.find(p => p.id === partyId);
        const randomOffsetLat = (Math.random() - 0.5) * 0.004;
        const randomOffsetLng = (Math.random() - 0.5) * 0.004;
        const simulatedLat = parseFloat(((baseParty ? baseParty.lat : 23.0225) + randomOffsetLat).toFixed(6));
        const simulatedLng = parseFloat(((baseParty ? baseParty.lng : 72.5714) + randomOffsetLng).toFixed(6));

        setTempGPS({ lat: simulatedLat, lng: simulatedLng });
        addToast("GPS permission blocked. Generated coordinate preview in Ahmedabad.", "success");
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  };

  const handleSaveGPS = (partyId) => {
    if (!tempGPS) return;
    setParties(prev => prev.map(p => {
      if (p.id === partyId) {
        return {
          ...p,
          lat: tempGPS.lat,
          lng: tempGPS.lng
        };
      }
      return p;
    }));

    dispatch(UpdateCollectionManLatLog({
      PartyID: partyId,
      Latitude: tempGPS.lat,
      Longitude: tempGPS.lng
    })).then(() => {
      dispatch(GetCollectionManParties());
    });

    addToast(`Saved new GPS coordinates for ${parties.find(p => p.id === partyId)?.name}!`, "success");
    setTempGPS(null);
  };

  const togglePartySelection = (partyId) => {
    setParties(prev => prev.map(p => {
      if (p.id === partyId) {
        return { ...p, selected: !p.selected };
      }
      return p;
    }));
  };

  const activeList = hasPartyWiseResponse ? partyWiseCollectionList : collectionList;

  const displayLedger = Array.isArray(activeList) && activeList.length > 0
    ? activeList.map((item, index) => {
      const id = item.ChqNo || item.InvoiceNo || index + 1;

      let cDate = item.CurrentDate || '';
      if (cDate && cDate.includes('T')) {
        cDate = cDate.split('T')[0];
      }

      let chqD = item.chqDate || '';
      if (chqD && chqD.includes('T')) {
        chqD = chqD.split('T')[0];
      }

      return {
        id,
        partyName: item.PartyName || partyFilter || '',
        currentDate: cDate,
        chqDate: chqD,
        payStyle: item.PayStyle || '',
        chqNo: item.ChqNo || '',
        chqBankName: item.ChqBankName || '',
        amount: parseFloat(item.Amount || item.amount || item.TotalOs || item.TotalOS || item.NetBal || item.Balance || item.Net || item.NetBalAmt || 0),
        remark: item.Remark || '',
        billImg: item.billImg || '0',
        Approval: item.Approval || ''
      };
    })
    : [];

  console.log(displayLedger);


  const totalPendingOutstanding = displayLedger.reduce((acc, curr) => acc + curr.amount, 0);

  return (
    <div className="collection-man-dashboard">

      {/* Sleek inline stylesheet matching screenshots exactly (Ignore Dynamic Application Themes) */}
      <style>{`
        .collection-man-dashboard {
          background-color: #f1f5f9 !important;
          color: #1e293b !important;
          font-family: 'NotoSans', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          min-height: 100vh;
          padding: 24px;
          position: relative;
        }

        /* Hover Tooltips for KPIs */
        .kpi-info-container {
           position: relative;
           display: inline-flex;
           align-items: center;
        }
        .kpi-info-tooltip {
           visibility: hidden;
           opacity: 0;
           width: max-content;
           min-width: 200px;
           background-color: #1e293b;
           color: #f8fafc !important;
           text-align: left;
           border-radius: 6px;
           padding: 8px 12px;
           position: absolute;
           z-index: 9999;
           top: 100%;
           left: 50%;
           transform: translateX(-50%) translateY(8px);
           transition: opacity 0.2s ease, transform 0.2s ease;
           font-size: 11px;
           line-height: 1.5;
           white-space: pre-wrap;
           box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
           border: 1px solid #334155;
           pointer-events: none;
           font-family: 'NotoSans', sans-serif;
           font-weight: 500;
        }
        .kpi-info-tooltip::after {
           content: "";
           position: absolute;
           bottom: 100%;
           left: 50%;
           margin-left: -6px;
           border-width: 6px;
           border-style: solid;
           border-color: transparent transparent #1e293b transparent;
        }
        .kpi-info-container:hover .kpi-info-tooltip {
           visibility: visible;
           opacity: 1;
           transform: translateX(-50%) translateY(4px);
        }

        /* Searchable Select Styles */
        .searchable-select-container {
          position: relative;
          width: 100%;
        }

        .searchable-select-trigger {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          cursor: pointer;
          text-align: left;
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          color: #0f172a !important;
          font-size: 13px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          user-select: none;
        }

        .searchable-select-trigger:focus,
        .searchable-select-trigger.open {
          border-color: #0ea5e9 !important;
          box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15) !important;
        }

        .searchable-select-trigger.filter-style {
          padding: 10px 12px;
          height: 40px;
        }

        .searchable-select-trigger.form-style {
          padding: 10px 12px 10px 38px;
          height: 40px;
        }

        .searchable-select-trigger .trigger-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .searchable-select-trigger .trigger-arrow {
          color: #64748b;
          margin-left: 8px;
          transition: transform 0.2s;
          flex-shrink: 0;
        }

        .searchable-select-trigger.open .trigger-arrow {
          transform: rotate(180deg);
        }

        .searchable-select-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 1000;
          margin-top: 4px;
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
          overflow: hidden;
          animation: searchableDropdownFadeIn 0.15s ease-out;
        }

        @keyframes searchableDropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .searchable-select-search-container {
          padding: 8px;
          border-bottom: 1px solid #f1f5f9;
          position: relative;
          display: flex;
          align-items: center;
        }

        .searchable-select-search-input {
          width: 100%;
          padding: 8px 8px 8px 32px;
          font-size: 13px;
          border: 1px solid #cbd5e1 !important;
          border-radius: 6px;
          background-color: #f8fafc !important;
          color: #0f172a !important;
          outline: none;
          transition: border-color 0.2s;
        }

        .searchable-select-search-input:focus {
          border-color: #0ea5e9 !important;
          background-color: #ffffff !important;
        }

        .searchable-select-search-icon {
          position: absolute;
          left: 18px;
          color: #64748b;
          pointer-events: none;
        }

        .searchable-select-options {
          max-height: 220px;
          overflow-y: auto;
          padding: 4px 0;
        }

        .searchable-select-options::-webkit-scrollbar {
          width: 6px;
        }
        .searchable-select-options::-webkit-scrollbar-track {
          background: transparent;
        }
        .searchable-select-options::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 3px;
        }

        .searchable-select-option {
          padding: 8px 12px;
          font-size: 13px;
          color: #334155;
          cursor: pointer;
          transition: background-color 0.15s, color 0.15s;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-align: left;
        }

        .searchable-select-option:hover {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }

        .searchable-select-option.selected {
          background-color: #e0f2fe !important;
          color: #0369a1 !important;
          font-weight: 600;
        }

        .searchable-select-no-results {
          padding: 16px 12px;
          font-size: 13px;
          color: #64748b;
          text-align: center;
          font-style: italic;
        }

        /* custom grid layout variables */
        .erp-card {
          background-color: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
          margin-bottom: 24px;
          padding: 20px;
          position: relative;
          transition: transform 0.2s ease, border-color 0.2s ease;
        }
        .erp-card:hover {
          border-color: #cbd5e1 !important;
        }

        .section-header {
          border-bottom: 1px solid #f1f5f9 !important;
          margin-bottom: 18px;
          padding-bottom: 12px;
        }
        .section-title {
          color: #0f172a !important;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 0.5px;
          margin: 0;
          text-transform: uppercase;
        }
        .section-subtitle {
          color: #64748b !important;
          font-size: 12px;
          margin-top: 4px;
        }

        /* Top Module Header Banner */
        .banner-row {
          align-items: center;
          background-color: rgba(255, 255, 255, 0.8) !important;
          border: 1px dashed #cbd5e1 !important;
          border-radius: 14px;
          display: flex;
          justify-content: space-between;
          margin-bottom: 24px;
          padding: 20px 24px;
        }
        .banner-title-area {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .banner-icon-box {
          align-items: center;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.15) 0%, rgba(14, 165, 233, 0.05) 100%) !important;
          border: 1px solid rgba(14, 165, 233, 0.3) !important;
          border-radius: 10px;
          color: #0ea5e9 !important;
          display: flex;
          justify-content: center;
          padding: 12px;
        }
        .banner-h1 {
          color: #0f172a !important;
          font-size: 20px;
          font-weight: 700;
          margin: 0;
        }
        .banner-desc {
          color: #64748b !important;
          font-size: 13px;
          margin-top: 4px;
        }
        .banner-actions {
          display: flex;
          gap: 12px;
        }

        /* Interactive Filter Bar */
        .filters-row {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px;
          display: grid;
          gap: 20px;
          grid-template-columns: repeat(4, 1fr);
          align-items: end;
          margin-bottom: 24px;
          padding: 16px 20px;
        }
        @media (max-width: 992px) {
          .filters-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 576px) {
          .filters-row {
            grid-template-columns: 1fr;
          }
        }
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .filter-label {
          color: #475569 !important;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }
        .filter-select {
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          color: #1e293b !important;
          font-size: 13px;
          outline: none;
          padding: 10px 12px;
          transition: border-color 0.2s;
          width: 100%;
        }
        .filter-select:focus {
          border-color: #0ea5e9 !important;
        }

        /* horizontal navigation sub-tabs */
        .subtabs-bar {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px;
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          overflow-x: auto;
          padding: 8px;
        }
        .subtab-btn {
          align-items: center;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 8px;
          color: #64748b !important;
          cursor: pointer;
          display: flex;
          font-size: 13px;
          font-weight: 600;
          gap: 8px;
          padding: 10px 18px;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .subtab-btn:hover {
          color: #0f172a !important;
        }
        .subtab-btn.active {
          background-color: #0ea5e9 !important;
          border-color: #0ea5e9 !important;
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25) !important;
        }

        /* Buttons styling */
        .btn-primary {
          align-items: center;
          background-color: #0ea5e9 !important;
          border: none;
          border-radius: 8px;
          color: #ffffff !important;
          cursor: pointer;
          display: flex;
          font-size: 13px;
          font-weight: 600;
          gap: 8px;
          justify-content: center;
          padding: 10px 20px;
          transition: all 0.2s;
        }
        .btn-primary:hover {
          background-color: #0284c7 !important;
          transform: translateY(-1px);
        }
        .btn-secondary {
          align-items: center;
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          color: #475569 !important;
          cursor: pointer;
          display: flex;
          font-size: 13px;
          font-weight: 600;
          gap: 8px;
          padding: 10px 16px;
          transition: all 0.2s;
        }
        .btn-secondary:hover {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .btn-danger {
          align-items: center;
          background-color: rgba(239, 68, 68, 0.08) !important;
          border: 1px solid rgba(239, 68, 68, 0.3) !important;
          border-radius: 8px;
          color: #ef4444 !important;
          cursor: pointer;
          display: flex;
          font-size: 13px;
          font-weight: 600;
          gap: 8px;
          padding: 10px 16px;
          transition: all 0.2s;
        }
        .btn-danger:hover {
          background-color: #ef4444 !important;
          color: #ffffff !important;
        }

        /* Interactive payment buttons */
        .toggle-options {
          display: flex;
          gap: 8px;
        }
        .toggle-opt-btn {
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          color: #475569 !important;
          cursor: pointer;
          flex: 1;
          font-size: 13px;
          font-weight: 600;
          padding: 10px;
          text-align: center;
          transition: all 0.2s;
        }
        .toggle-opt-btn.active {
          background-color: #e0f2fe !important;
          border-color: #0ea5e9 !important;
          color: #0369a1 !important;
          box-shadow: inset 0 0 4px rgba(14, 165, 233, 0.1) !important;
        }

        /* Forms Layout & Inputs */
        .erp-form {
          display: grid;
          gap: 20px;
          grid-template-columns: repeat(3, 1fr);
        }
        @media (max-width: 992px) {
          .erp-form {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 768px) {
          .erp-form {
            grid-template-columns: 1fr;
          }
        }
        .form-full-width {
          grid-column: 1 / -1;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .form-label {
          color: #475569 !important;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }
        .input-wrapper {
          position: relative;
        }
        .input-icon {
          color: #64748b !important;
          left: 12px;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
        }
        .form-input {
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          color: #0f172a !important;
          font-size: 13px;
          outline: none;
          padding: 10px 12px 10px 38px;
          transition: border-color 0.2s;
          width: 100%;
        }
        .form-input:focus {
          border-color: #0ea5e9 !important;
        }
        
        /* Drag Drop Upload */
        .upload-dropzone {
          align-items: center;
          background-color: #f8fafc !important;
          border: 2px dashed #cbd5e1 !important;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 30px 20px;
          text-align: center;
          transition: all 0.2s;
        }
        .upload-dropzone:hover {
          border-color: #0ea5e9 !important;
          background-color: rgba(14, 165, 233, 0.02) !important;
        }
        .upload-icon {
          color: #0ea5e9 !important;
          margin-bottom: 12px;
        }
        .upload-main-text {
          font-size: 13px;
          font-weight: 600;
          margin: 0;
          color: #0f172a !important;
        }
        .upload-sub-text {
          color: #64748b !important;
          font-size: 11px;
          margin-top: 4px;
        }

        /* Outstanding Intelligence Card */
        .outstanding-box {
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-left: 4px solid #ef4444 !important;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        .outstanding-left {
          display: flex;
          flex-direction: column;
        }
        .outstanding-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748b !important;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .outstanding-val {
          color: #0f172a !important;
          font-size: 32px;
          font-weight: 800;
          margin-top: 4px;
        }
        .outstanding-sub {
          color: #64748b !important;
          font-size: 12px;
          margin-top: 2px;
        }
        .outstanding-mid {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .badge-red {
          background-color: rgba(239, 68, 68, 0.08) !important;
          border: 1px solid rgba(239, 68, 68, 0.2) !important;
          border-radius: 20px;
          color: #ef4444 !important;
          font-size: 12px;
          font-weight: 700;
          padding: 4px 12px;
        }
        .badge-orange {
          background-color: rgba(249, 115, 22, 0.08) !important;
          border: 1px solid rgba(249, 115, 22, 0.2) !important;
          border-radius: 20px;
          color: #ea580c !important;
          font-size: 12px;
          font-weight: 700;
          padding: 4px 12px;
        }
        .badge-green {
          background-color: rgba(34, 197, 94, 0.08) !important;
          border: 1px solid rgba(34, 197, 94, 0.2) !important;
          border-radius: 20px;
          color: #22c55e !important;
          font-size: 12px;
          font-weight: 700;
          padding: 4px 12px;
        }
        .badge-yellow {
          background-color: rgba(245, 158, 11, 0.08) !important;
          border: 1px solid rgba(245, 158, 11, 0.2) !important;
          border-radius: 20px;
          color: #f59e0b !important;
          font-size: 12px;
          font-weight: 700;
          padding: 4px 12px;
        }
        .badge-blue {
          background-color: rgba(59, 130, 246, 0.08) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 20px;
          color: #3b82f6 !important;
          font-size: 12px;
          font-weight: 700;
          padding: 4px 12px;
        }
        
        /* Modern ERP Table Styles */
        .table-responsive {
          overflow-x: auto;
          width: 100%;
        }
        .erp-table {
          border-collapse: collapse;
          width: 100%;
          text-align: left;
        }
        .erp-table th {
          border-bottom: 2px solid #cbd5e1 !important;
          color: #475569 !important;
          background-color: #f8fafc !important;
          font-size: 11px;
          font-weight: 700;
          padding: 12px 16px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .erp-table td {
          border-bottom: 1px solid #cbd5e1 !important;
          color: #334155 !important;
          font-size: 13px;
          padding: 14px 16px;
        }
        .erp-table tr:hover td {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        
        .currency-green {
          color: #22c55e !important;
          font-weight: 600;
        }
        .currency-red {
          color: #ef4444 !important;
          font-weight: 600;
        }

        /* KPI Cards Grid */
        .kpi-row {
          display: grid;
          gap: 20px;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          margin-bottom: 24px;
        }
        .kpi-card {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px;
          padding: 18px 20px;
          display: flex;
          flex-direction: column;
          position: relative;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
        }
        .kpi-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #64748b !important;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .kpi-icon {
          color: #64748b !important;
        }
        .kpi-val {
          color: #0f172a !important;
          font-size: 22px;
          font-weight: 800;
          margin-top: 8px;
        }
        .kpi-sub {
          color: #64748b !important;
          font-size: 11px;
          margin-top: 4px;
        }

        /* Master-Detail History Styles */
        .history-container {
          display: grid;
          gap: 24px;
          grid-template-columns: 280px 1fr;
        }
        @media(max-width: 768px) {
          .history-container {
            grid-template-columns: 1fr;
          }
        }
        
        .party-list-box {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
        }
        .party-list-title {
          font-size: 12px;
          font-weight: 700;
          color: #475569 !important;
          text-transform: uppercase;
          margin-bottom: 12px;
        }
        .party-item {
          border-radius: 8px;
          cursor: pointer;
          padding: 12px;
          transition: all 0.2s;
          border: 1px solid transparent;
          margin-bottom: 8px;
        }
        .party-item:hover {
          background-color: #f1f5f9 !important;
        }
        .party-item.active {
          background-color: #e0f2fe !important;
          border-color: rgba(14, 165, 233, 0.4) !important;
        }
        .party-item-name {
          color: #0f172a !important;
          font-size: 13px;
          font-weight: 700;
        }
        .party-item-meta {
          color: #64748b !important;
          font-size: 11px;
          margin-top: 4px;
          display: flex;
          justify-content: space-between;
        }

        /* Collapse Timeline Cards */
        .visit-timeline-card {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          margin-bottom: 16px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02) !important;
        }
        .visit-timeline-header {
          align-items: center;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          padding: 16px;
          transition: background-color 0.2s;
        }
        .visit-timeline-header:hover {
          background-color: #f8fafc !important;
        }
        .visit-badge-col {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .visit-count-tag {
          background-color: #fef08a !important;
          color: #854d0e !important;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 800;
          padding: 4px 8px;
          text-align: center;
        }
        .visit-count-tag.green {
          background-color: #dcfce7 !important;
          color: #166534 !important;
        }
        .timeline-body-wrap {
          border-top: 1px solid #cbd5e1 !important;
          padding: 16px;
          background-color: #f8fafc !important;
        }
        
        .timeline-steps {
          border-left: 2px solid #cbd5e1 !important;
          margin-left: 8px;
          padding-left: 16px;
          position: relative;
        }
        .timeline-step {
          margin-bottom: 14px;
          position: relative;
        }
        .timeline-step::before {
          background-color: #0ea5e9 !important;
          border: 3px solid #ffffff !important;
          border-radius: 50%;
          content: '';
          height: 12px;
          left: -23px;
          position: absolute;
          top: 3px;
          width: 12px;
        }
        .timeline-step-time {
          font-size: 11px;
          font-weight: 700;
          color: #0ea5e9 !important;
        }
        .timeline-step-desc {
          font-size: 12px;
          color: #475569 !important;
          margin-top: 2px;
        }

        /* Schedule logs list */
        .schedule-scroll-area {
          max-height: 480px;
          overflow-y: auto;
          padding-right: 4px;
        }
        .schedule-item {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 18px;
          margin-bottom: 12px;
          transition: all 0.2s;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02) !important;
        }
        .schedule-item:hover {
          border-color: rgba(14, 165, 233, 0.3) !important;
        }
        .schedule-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .schedule-date-block {
          background-color: #f1f5f9 !important;
          border-radius: 6px;
          color: #0f172a !important;
          font-size: 11px;
          font-weight: 800;
          padding: 8px;
          text-align: center;
          width: 54px;
          border: 1px solid #cbd5e1 !important;
        }
        .schedule-party-info {
          display: flex;
          flex-direction: column;
        }
        .schedule-party-name {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a !important;
        }
        .schedule-party-meta {
          font-size: 11px;
          color: #64748b !important;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        /* Modal Popup styles */
        .modal-overlay {
          background-color: rgba(15, 23, 42, 0.6) !important;
          backdrop-filter: blur(4px);
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
        }
        .modal-card {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15) !important;
          max-width: 500px;
          width: 100%;
          padding: 24px;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #cbd5e1 !important;
          padding-bottom: 12px;
          margin-bottom: 20px;
        }
        .modal-title {
          font-size: 16px;
          font-weight: 700;
          color: #0f172a !important;
          margin: 0;
        }
        .modal-close {
          background: transparent;
          border: none;
          color: #64748b;
          font-size: 24px;
          cursor: pointer;
        }

        /* Light Theme Schedule Modal Styles (Cohesive with Light Theme Canvas) */
        .schedule-modal-overlay {
          background-color: rgba(15, 23, 42, 0.5) !important; /* Soft light backdrop */
          backdrop-filter: blur(4px);
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
        }

        .schedule-modal-card {
          background-color: #ffffff !important; /* White matching standard erp-cards */
          border: 1px solid #cbd5e1 !important; /* Slate border */
          border-radius: 16px !important;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1) !important;
          max-width: 520px !important;
          width: 100% !important;
          padding: 24px 28px !important;
          color: #0f172a !important; /* Dark text */
        }

        .schedule-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 1px solid #e2e8f0 !important;
          padding-bottom: 14px !important;
          margin-bottom: 20px !important;
          width: 100%;
        }

        .schedule-modal-title {
          font-size: 16px !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          margin: 0 !important;
        }

        .schedule-modal-subtitle {
          color: #64748b !important;
          font-size: 12px !important;
          margin-top: 4px !important;
          text-align: left !important;
        }

        .schedule-modal-close {
          background: transparent !important;
          border: none !important;
          color: #94a3b8 !important;
          font-size: 22px !important;
          line-height: 1 !important;
          cursor: pointer !important;
          transition: color 0.2s !important;
          padding: 0 !important;
        }
        .schedule-modal-close:hover {
          color: #0f172a !important;
        }

        .schedule-form-group {
          display: flex !important;
          flex-direction: column !important;
          gap: 6px !important;
          text-align: left !important;
        }

        .schedule-form-label {
          color: #475569 !important; /* Dark label text */
          font-size: 11px !important;
          font-weight: 700 !important;
          letter-spacing: 0.5px !important;
          text-transform: uppercase !important;
          margin-bottom: 2px !important;
        }

        .schedule-input-wrapper {
          position: relative !important;
        }

        .schedule-input-icon {
          color: #64748b !important; /* Slate icon color */
          left: 14px !important;
          position: absolute !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          pointer-events: none !important;
        }

        .schedule-form-input {
          background-color: #f8fafc !important; /* Light background */
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          color: #0f172a !important;
          font-size: 13px !important;
          outline: none !important;
          padding: 10px 14px 10px 42px !important;
          transition: all 0.2s ease !important;
          width: 100% !important;
          height: 42px !important;
        }

        .schedule-form-input-no-icon {
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          color: #0f172a !important;
          font-size: 13px !important;
          outline: none !important;
          padding: 10px 14px !important;
          transition: all 0.2s ease !important;
          width: 100% !important;
          height: 42px !important;
        }

        .schedule-form-input:focus, 
        .schedule-form-input-no-icon:focus {
          border-color: #0ea5e9 !important;
          box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15) !important;
          background-color: #ffffff !important;
        }

        .schedule-form-select {
          appearance: none !important;
          background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23475569' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E") !important;
          background-position: right 14px center !important;
          background-repeat: no-repeat !important;
          background-size: 18px !important;
          padding-right: 40px !important;
        }

        .schedule-btn-cancel {
          background-color: #f1f5f9 !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 9999px !important;
          color: #475569 !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          padding: 10px 24px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
        }
        .schedule-btn-cancel:hover {
          background-color: #e2e8f0 !important;
          color: #0f172a !important;
        }

        .schedule-btn-submit {
          background-color: #0ea5e9 !important; /* Sky-blue */
          border: none !important;
          border-radius: 9999px !important;
          color: #ffffff !important;
          font-size: 13px !important;
          font-weight: 700 !important;
          padding: 10px 24px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.2) !important;
        }
        .schedule-btn-submit:hover {
          background-color: #0284c7 !important;
          box-shadow: 0 6px 16px rgba(14, 165, 233, 0.3) !important;
        }        /* Calendar View Layout */
        .calendar-layout-grid {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) 340px !important;
          gap: 16px !important;
          margin-top: 10px !important;
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        @media (max-width: 1100px) {
          .calendar-layout-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }

        .calendar-main-card {
          background-color: #ffffff !important; /* White background matching rest of page cards */
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px !important;
          padding: 16px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
          min-width: 0 !important;
          width: 100% !important;
          box-sizing: border-box !important;
          overflow-x: auto !important;
        }

        .calendar-header-row {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          flex-wrap: wrap !important;
          gap: 12px !important;
          margin-bottom: 16px !important;
        }

        .calendar-month-title {
          color: #0f172a !important; /* Dark text */
          font-size: 16px !important;
          font-weight: 700 !important;
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
        }

        .calendar-nav-btn {
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          color: #475569 !important;
          cursor: pointer !important;
          padding: 6px 12px !important;
          border-radius: 6px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s ease !important;
        }
        .calendar-nav-btn:hover {
          background-color: #e2e8f0 !important;
          color: #0f172a !important;
        }

        .calendar-legends {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 12px !important;
          font-size: 11px !important;
          color: #64748b !important;
        }
        .calendar-legend-item {
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
        }

        .calendar-grid-header {
          display: grid !important;
          grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
          text-align: center !important;
          margin-bottom: 8px !important;
          min-width: 480px !important;
        }
        .calendar-weekday-label {
          color: #475569 !important; /* Highly visible slate color */
          font-size: 11px !important;
          font-weight: 700 !important;
          padding: 8px 0 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
        }

        .calendar-days-grid {
          display: grid !important;
          grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
          gap: 6px !important;
          min-width: 480px !important;
        }

        .calendar-day-cell {
          background-color: #ffffff !important; /* White day cell */
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          min-height: 85px !important;
          padding: 6px !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          position: relative !important;
          min-width: 0 !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }
        .calendar-day-cell:hover {
          border-color: #94a3b8 !important;
          background-color: #f8fafc !important;
        }
        .calendar-day-cell.selected {
          border-color: #0ea5e9 !important; /* Premium blue outline for selected cell */
          box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15) !important;
          background-color: #f0f9ff !important; /* Soft blue tint */
        }
        .calendar-day-cell.empty {
          background-color: transparent !important;
          border: none !important;
          pointer-events: none !important;
        }

        .calendar-day-num-row {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          margin-bottom: 4px !important;
        }
        .calendar-day-number {
          color: #475569 !important; /* Visible slate number */
          font-size: 12px !important;
          font-weight: 700 !important;
        }
        .calendar-day-cell.selected .calendar-day-number {
          color: #0369a1 !important; /* Blue color for selected day number */
        }

        .calendar-today-badge {
          background-color: #0ea5e9 !important;
          color: #ffffff !important;
          font-size: 8px !important;
          font-weight: 800 !important;
          padding: 1px 4px !important;
          border-radius: 4px !important;
          text-transform: uppercase !important;
        }

        /* Event capsule badges inside cells (Priority-based) */
        .calendar-event-capsule {
          font-size: 9px !important;
          font-weight: 600 !important;
          padding: 2px 6px !important;
          border-radius: 4px !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          display: flex !important;
          align-items: center !important;
          gap: 4px !important;
          max-width: 100% !important;
        }
        .calendar-event-capsule.critical,
        .calendar-sidebar-badge.critical {
          background-color: rgba(239, 68, 68, 0.15) !important;
          color: #dc2626 !important;
          border: 1px solid rgba(239, 68, 68, 0.3) !important;
        }
        .calendar-event-capsule.high,
        .calendar-sidebar-badge.high {
          background-color: rgba(249, 115, 22, 0.15) !important;
          color: #ea580c !important;
          border: 1px solid rgba(249, 115, 22, 0.3) !important;
        }
        .calendar-event-capsule.normal,
        .calendar-sidebar-badge.normal {
          background-color: rgba(14, 165, 233, 0.15) !important;
          color: #0284c7 !important;
          border: 1px solid rgba(14, 165, 233, 0.3) !important;
        }
        .calendar-event-capsule.low,
        .calendar-sidebar-badge.low {
          background-color: rgba(34, 197, 94, 0.15) !important;
          color: #16a34a !important;
          border: 1px solid rgba(34, 197, 94, 0.3) !important;
        }

        .calendar-more-events {
          color: #64748b !important;
          font-size: 8px !important;
          font-weight: 700 !important;
          margin-top: auto !important;
          padding-left: 2px !important;
        }

        /* Sidebar Visit Details panel */
        .calendar-sidebar-card {
          background-color: #ffffff !important; /* White matching the rest */
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px !important;
          padding: 20px !important;
          display: flex !important;
          flex-direction: column !important;
          height: 100% !important;
          max-height: 575px !important;
          overflow-y: auto !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
        }
        
        .calendar-sidebar-header {
          border-bottom: 1px solid #e2e8f0 !important;
          padding-bottom: 12px !important;
          margin-bottom: 16px !important;
          text-align: left !important;
        }
        
        .calendar-sidebar-meta-status {
          font-size: 10px !important;
          font-weight: 700 !important;
          color: #64748b !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
        }

        .calendar-sidebar-title {
          color: #0f172a !important; /* Dark text */
          font-size: 16px !important;
          font-weight: 700 !important;
          margin: 4px 0 2px 0 !important;
        }
        .calendar-sidebar-subtitle {
          color: #64748b !important;
          font-size: 11px !important;
          margin: 0 !important;
        }

        /* Sidebar list cards */
        .calendar-sidebar-item {
          background-color: #f8fafc !important; /* Elegant light-gray card background */
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          padding: 14px !important;
          margin-bottom: 12px !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 8px !important;
          transition: all 0.2s ease !important;
          text-align: left !important;
        }
        .calendar-sidebar-item:hover {
          border-color: #cbd5e1 !important;
          background-color: #f1f5f9 !important;
        }

        .calendar-sidebar-item-top {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
        }

        .calendar-sidebar-party-name {
          color: #0f172a !important; /* Dark text */
          font-size: 13px !important;
          font-weight: 700 !important;
        }

        .calendar-sidebar-badge {
          font-size: 9px !important;
          font-weight: 700 !important;
          padding: 2px 8px !important;
          border-radius: 20px !important;
        }
        .calendar-sidebar-badge.critical {
          background-color: rgba(239, 68, 68, 0.1) !important;
          border: 1px solid rgba(239, 68, 68, 0.3) !important;
          color: #dc2626 !important;
        }
        .calendar-sidebar-badge.high {
          background-color: rgba(249, 115, 22, 0.1) !important;
          border: 1px solid rgba(249, 115, 22, 0.3) !important;
          color: #ea580c !important;
        }
        .calendar-sidebar-badge.normal {
          background-color: rgba(14, 165, 233, 0.1) !important;
          border: 1px solid rgba(14, 165, 233, 0.3) !important;
          color: #0284c7 !important;
        }
        .calendar-sidebar-badge.low {
          background-color: rgba(34, 197, 94, 0.1) !important;
          border: 1px solid rgba(34, 197, 94, 0.3) !important;
          color: #16a34a !important;
        }

        .calendar-sidebar-item-meta {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 12px !important;
          color: #64748b !important; /* Visible text slate */
          font-size: 11px !important;
        }

        .calendar-sidebar-meta-col {
          display: flex !important;
          align-items: center !important;
          gap: 4px !important;
        }

        .calendar-sidebar-item-remarks {
          border-top: 1px dashed #e2e8f0 !important;
          padding-top: 8px !important;
          margin-top: 2px !important;
          color: #64748b !important;
          font-size: 11px !important;
          font-style: italic !important;
        }

        /* Toggle display mode button group style */
        .toggle-view-group {
          display: flex !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          overflow: hidden !important;
          background-color: #f8fafc !important;
          height: 36px !important;
        }
        .toggle-view-btn {
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
          padding: 0 16px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #475569 !important;
          border: none !important;
          background: transparent !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
        }
        .toggle-view-btn:hover {
          color: #0f172a !important;
        }
        .toggle-view-btn.active {
          background-color: #0ea5e9 !important;
          color: #ffffff !important;
          box-shadow: 0 2px 8px rgba(14, 165, 233, 0.2) !important;
        }

        /* Toasts Container */
        .toast-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 99999;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .custom-toast {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 8px;
          color: #ffffff !important;
          font-size: 13px;
          font-weight: 600;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
          animation: slideIn 0.3s ease-out;
        }
        .custom-toast.success {
          background-color: #059669 !important;
          border: 1px solid #34d399 !important;
        }
        .custom-toast.error {
          background-color: #dc2626 !important;
          border: 1px solid #f87171 !important;
        }
        .toast-icon {
          display: flex;
          align-items: center;
        }
        .toast-close {
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.8) !important;
          font-size: 18px;
          cursor: pointer;
          margin-left: auto;
        }
        
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* Scrollbar styles override */
        .schedule-scroll-area::-webkit-scrollbar,
        .subtabs-bar::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }
        .schedule-scroll-area::-webkit-scrollbar-track,
        .subtabs-bar::-webkit-scrollbar-track {
          background: #f1f5f9 !important;
        }
        .schedule-scroll-area::-webkit-scrollbar-thumb,
        .subtabs-bar::-webkit-scrollbar-thumb {
          background: #cbd5e1 !important;
          border-radius: 4px;
        }
        .schedule-scroll-area::-webkit-scrollbar-thumb:hover,
        .subtabs-bar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8 !important;
        }

        /* GPS Lat/Log Tracking Section Styles */
        .latlog-split-container {
          display: grid !important;
          grid-template-columns: 340px 1fr !important;
          gap: 24px !important;
          margin-top: 10px !important;
        }
        @media (max-width: 992px) {
          .latlog-split-container {
            grid-template-columns: 1fr !important;
          }
        }
        .latlog-list-card {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px !important;
          padding: 16px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
          display: flex !important;
          flex-direction: column !important;
          max-height: 520px !important;
        }
        .latlog-search-wrapper {
          position: relative !important;
          margin-bottom: 12px !important;
        }
        .latlog-search-input {
          background-color: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          color: #0f172a !important;
          font-size: 13px !important;
          outline: none !important;
          padding: 8px 10px 8px 34px !important;
          width: 100% !important;
        }
        .latlog-search-input:focus {
          border-color: #0ea5e9 !important;
        }
        .latlog-scroll-area {
          overflow-y: auto !important;
          flex: 1 !important;
          padding-right: 4px !important;
        }
        .latlog-party-item {
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          padding: 12px !important;
          margin-bottom: 8px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          background-color: #ffffff !important;
          text-align: left !important;
          display: flex !important;
          align-items: flex-start !important;
          gap: 10px !important;
        }
        .latlog-party-item:hover {
          border-color: #94a3b8 !important;
          background-color: #f8fafc !important;
        }
        .latlog-party-item.active {
          border-color: #0ea5e9 !important;
          background-color: #f0f9ff !important;
        }
        .latlog-party-checkbox {
          margin-top: 3px !important;
          width: 16px !important;
          height: 16px !important;
          cursor: pointer !important;
        }
        .latlog-party-name {
          font-size: 13px !important;
          font-weight: 700 !important;
          color: #0f172a !important;
        }
        .latlog-party-area {
          font-size: 11px !important;
          color: #64748b !important;
          margin-top: 2px !important;
        }
        .latlog-party-coords {
          font-size: 11px !important;
          color: #0284c7 !important;
          font-weight: 600 !important;
          margin-top: 4px !important;
          display: flex !important;
          gap: 8px !important;
        }
        .latlog-map-card {
          background-color: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px !important;
          padding: 20px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
          display: flex !important;
          flex-direction: column !important;
          height: 520px !important;
        }
        .latlog-map-viewbox {
          flex: 1 !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          background-color: #f8fafc !important;
          overflow: hidden !important;
          position: relative !important;
        }
        .latlog-action-bar {
          display: flex !important;
          gap: 12px !important;
          margin-top: 14px !important;
          justify-content: flex-end !important;
        }
        .latlog-pulse-dot {
          animation: pulseMarker 1.8s infinite !important;
        }
        @keyframes pulseMarker {
          0% { transform: scale(0.9); opacity: 0.9; }
          50% { transform: scale(1.2); opacity: 0.5; }
          100% { transform: scale(0.9); opacity: 0.9; }
        }
        
        /* Leaflet Custom overrides */
        .leaflet-container {
          font-family: inherit !important;
          border-radius: 8px !important;
        }
        .leaflet-bar {
          border: 1px solid #cbd5e1 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
        }
        .leaflet-bar a {
          background-color: #ffffff !important;
          color: #475569 !important;
          border-bottom: 1px solid #cbd5e1 !important;
          transition: background-color 0.2s !important;
        }
        .leaflet-bar a:hover {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 8px !important;
          box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
          border: 1px solid #cbd5e1 !important;
          background-color: #ffffff !important;
          color: #0f172a !important;
          padding: 4px !important;
        }
        .leaflet-popup-tip {
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
        }

        /* Bucket Config Modal Styles */
        .bucket-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }
        .bucket-modal-content {
          background-color: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          width: 650px;
          max-width: 90vw;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          font-family: inherit;
        }
        .bucket-modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #cbd5e1;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          background-color: #f8fafc;
        }
        .bucket-modal-body {
          padding: 24px;
        }
        .bucket-modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #cbd5e1;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          background-color: #f8fafc;
        }
        .bucket-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        .bucket-table th {
          text-align: left;
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
          padding-bottom: 12px;
          border-bottom: 1px solid #cbd5e1;
        }
        .bucket-table td {
          padding: 12px 0;
          border-bottom: 1px solid #cbd5e1;
        }
        .bucket-table tr:last-child td {
          border-bottom: none;
        }
        .bucket-input {
          background-color: #ffffff;
          border: 1px solid #cbd5e1;
          color: #0f172a;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 13px;
          width: 100%;
          outline: none;
        }
        .bucket-input:focus {
          border-color: #3b82f6;
        }
        .bucket-input-number {
          width: 70px;
          text-align: center;
        }
        .bucket-btn-add {
          background: transparent;
          border: 1px solid #cbd5e1;
          color: #f59e0b;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 12px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
        }
        .bucket-btn-add:hover {
          background-color: #f8fafc;
        }
        .bucket-btn-icon {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 4px;
        }
        .bucket-btn-icon:hover {
          color: #ef4444;
          background-color: rgba(239, 68, 68, 0.1);
        }
        .bucket-unbounded {
          background-color: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 6px;
          padding: 6px;
          font-size: 11px;
          font-weight: bold;
          text-align: center;
          width: 100px;
        }
      `}</style>

      {/* Top Banner Navigation Row */}
      <div className="banner-row" id="dashboard-header-banner">
        <div className="banner-title-area">
          <div className="banner-icon-box">
            {activeSubTab === 'daily' && <DollarSign size={24} />}
            {activeSubTab === 'aging' && <TrendingUp size={24} />}
            {activeSubTab === 'grey' && <Briefcase size={24} />}
            {activeSubTab === 'history' && <FileText size={24} />}
            {activeSubTab === 'schedule' && <Calendar size={24} />}
            {['area', 'route', 'adv'].includes(activeSubTab) && <Settings size={24} />}
            {activeSubTab === 'latlog' && <MapPin size={24} />}
          </div>
          <div>
            <h1 className="banner-h1">
              {activeSubTab === 'daily' && "Daily Collection Entry"}
              {activeSubTab === 'aging' && "Aging Analysis Overview"}
              {activeSubTab === 'grey' && "Grey Balance Monitor"}
              {activeSubTab === 'history' && "Customer Payment History"}
              {activeSubTab === 'schedule' && "Schedule Visit Panel"}
              {activeSubTab === 'latlog' && "Get GPS Latitude & Longitude"}
              {activeSubTab === 'route' && "Route Management Controller"}
              {activeSubTab === 'adv' && "Advanced ERP Intelligence Reports"}
            </h1>
            <p className="banner-desc">
              {activeSubTab === 'daily' && "Capture field transactions and manage outstanding payment records"}
              {activeSubTab === 'aging' && "Outstanding payment aging distribution by production master"}
              {activeSubTab === 'grey' && "Monitor meter-wise grey balance and financial indicators"}
              {activeSubTab === 'history' && "Chronological collection history log by verified customers"}
              {activeSubTab === 'schedule' && "Plan, assign, and track customer visit schedules"}
              {['area', 'route', 'adv'].includes(activeSubTab) && "Custom analytics module for accounting optimization"}
              {activeSubTab === 'latlog' && "Party-wise GPS coordinates logging and beat map visualizer"}
            </p>
          </div>
        </div>
      </div>

      {/* Global Filter Bar (Drop-Downs) */}
      <div
        className="filters-row"
        id="global-erp-filters"
        style={partyFilter && Array.isArray(partiesList) && partiesList.find(p => p.PartyName === partyFilter) ? { paddingBottom: '70px', transition: 'padding-bottom 0.3s ease' } : { transition: 'padding-bottom 0.3s ease' }}
      >
        <div className="filter-group">
          <label className="filter-label">Market</label>
          <SearchableSelect
            id="filter-market"
            placeholder="Select Market"
            options={uniqueMarkets}
            value={marketFilter}
            onChange={(val) => {
              setMarketFilter(val);
              setPartyFilter('');
              setProdGroupFilter('');
            }}
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">Party Name</label>
          <SearchableSelect
            id="filter-party"
            placeholder="Select Party Name"
            options={filteredPartiesForDropdown}
            value={partyFilter}
            getLabel={(p) => p.PartyName}
            getValue={(p) => p.PartyName}
            onChange={(val) => {
              setPartyFilter(val);
              setProdGroupFilter([]);
            }}
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">Production Group</label>
          <SearchableSelect
            id="filter-prod-group"
            placeholder="Select Production Group"
            options={uniqueProductionGroups}
            value={prodGroupFilter}
            isMulti={true}
            onChange={(val) => {
              setProdGroupFilter(val);
            }}
          />
        </div>

        <div className="filter-group" style={{ position: 'relative' }}>
          <button
            type="button"
            className="btn-primary"
            id="filter-submit-btn"
            style={{ width: '100%', height: '40px' }}
            onClick={applyFilters}
          >
            Submit
          </button>

          {(() => {
            const selectedPartyForBal = Array.isArray(partiesList) ? partiesList.find(p => p.PartyName === partyFilter) : null;
            if (partyFilter && selectedPartyForBal) {
              return (
                <div style={{ position: 'absolute', top: 'calc(100% + 12px)', left: 0, right: 0, display: 'flex', gap: '8px', zIndex: 10 }}>
                  <div style={{ flex: 1, backgroundColor: '#f0f9ff', border: '1px solid #7dd3fc', borderRadius: '8px', padding: '6px 4px', color: '#0284c7', fontWeight: 'bold', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '2px', boxShadow: '0 4px 6px rgba(14, 165, 233, 0.1)' }}>
                    <span style={{ fontSize: '9px', color: '#0ea5e9', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Balance</span>
                    <span style={{ fontSize: '12px' }}>{formatAsINR(selectedPartyForBal.Bal || 0)}</span>
                  </div>
                  <div style={{ flex: 1, backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '6px 4px', color: '#dc2626', fontWeight: 'bold', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '2px', boxShadow: '0 4px 6px rgba(239, 68, 68, 0.1)' }}>
                    <span style={{ fontSize: '9px', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Grey Bal</span>
                    <span style={{ fontSize: '12px' }}>{Number(selectedPartyForBal.GryBal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {/* 10 Navigation sub-tabs (Switch View on Click) */}
      <div className="subtabs-bar" id="subtab-navigation-bar">
        <button className={`subtab-btn ${activeSubTab === 'daily' ? 'active' : ''}`} id="tab-daily" onClick={() => setActiveSubTab('daily')}>
          <DollarSign size={14} />
          Daily Collection Entry
        </button>
        <button className={`subtab-btn ${activeSubTab === 'aging' ? 'active' : ''}`} id="tab-aging" onClick={() => setActiveSubTab('aging')}>
          <TrendingUp size={14} />
          Aging Analysis
        </button>
        <button className={`subtab-btn ${activeSubTab === 'grey' ? 'active' : ''}`} id="tab-grey" onClick={() => setActiveSubTab('grey')}>
          <Briefcase size={14} />
          Grey Balance
        </button>
        <button className={`subtab-btn ${activeSubTab === 'history' ? 'active' : ''}`} id="tab-history" onClick={() => setActiveSubTab('history')}>
          <FileText size={14} />
          Payment History
        </button>
        <button className={`subtab-btn ${activeSubTab === 'schedule' ? 'active' : ''}`} id="tab-schedule" onClick={() => setActiveSubTab('schedule')}>
          <Calendar size={14} />
          Schedule Visit
        </button>
        <button className={`subtab-btn ${activeSubTab === 'latlog' ? 'active' : ''}`} id="tab-latlog" onClick={() => setActiveSubTab('latlog')}>
          <Route size={14} />
          Get Lat Log
        </button>
        <button className={`subtab-btn ${activeSubTab === 'route' ? 'active' : ''}`} id="tab-route" onClick={() => setActiveSubTab('route')}>
          <MapPin size={14} />
          Route Management
        </button>
      </div>

      {/* Main Dynamic View Panels */}

      {/* ========================================================
          TAB 1: DAILY COLLECTION ENTRY VIEW 
          ======================================================== */}
      {activeSubTab === 'daily' && (
        <div className="tab-view-panel" id="view-daily-entry">

          {/* Outstanding Intelligence Banner */}
          <div className="erp-card outstanding-box" id="outstanding-intel-card">
            <div className="outstanding-left">
              <div className="outstanding-title-row">
                Total Collection
              </div>
              <div className="outstanding-val">{formatAsINR(totalPendingOutstanding)}</div>
            </div>
          </div>

          {/* Add New Payment Card */}
          {hasPartyWiseResponse && (
            <div className="erp-card">
              <div className="section-header">
                <h2 className="section-title">Add New Payment Entry</h2>
                <p className="section-subtitle">Input transaction information and upload cheque copy/NEFT receipt</p>
              </div>

              <form className="erp-form" onSubmit={handleSavePayment}>
                {/* Payment type selector */}
                <div className="form-group">
                  <label className="form-label">Payment Type</label>
                  <div className="input-wrapper">
                    <List className="input-icon" size={16} style={{ zIndex: 10 }} />
                    <SearchableSelect
                      id="payment-type-select"
                      placeholder="-- Select Payment Type --"
                      options={paymentTypesList}
                      value={paymentType}
                      isFormStyle={true}
                      getLabel={(p) => p.PayStyle || p.PaymentStyle || 'Payment Style'}
                      getValue={(p) => p.PayStyleID !== undefined ? p.PayStyleID : p.PayStyle}
                      onChange={(val) => setPaymentType(val)}
                    />
                  </div>
                </div>

                {/* Reference Number input */}
                <div className="form-group">
                  <label className="form-label">Cheque / Ref No</label>
                  <div className="input-wrapper">
                    <FileText className="input-icon" size={16} />
                    <input
                      type="text"
                      className="form-input"
                      id="payment-ref-no"
                      placeholder="Enter reference number"
                      value={paymentRefNo}
                      onChange={(e) => setPaymentRefNo(e.target.value)}
                    />
                  </div>
                </div>

                {/* Cheque Date input */}
                <div className="form-group">
                  <label className="form-label">Cheque Date</label>
                  <div className="input-wrapper">
                    <Calendar className="input-icon" size={16} />
                    <input
                      type="date"
                      className="form-input"
                      id="payment-date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* Bank Name input */}
                <div className="form-group">
                  <label className="form-label">Bank Name</label>
                  <div className="input-wrapper">
                    <Briefcase className="input-icon" size={16} style={{ zIndex: 10 }} />
                    <SearchableSelect
                      id="payment-bank-name"
                      placeholder="-- Select Bank --"
                      options={banksList}
                      value={paymentBank}
                      isFormStyle={true}
                      getLabel={(b) => b.BankName || b.AcName || b.Bank || b.Name || 'Bank'}
                      getValue={(b) => b.BankName || b.AcName || b.Bank || b.Name || 'Bank'}
                      onChange={(val) => setPaymentBank(val)}
                    />
                  </div>
                </div>

                {/* Amount input */}
                <div className="form-group">
                  <label className="form-label">Amount (₹)</label>
                  <div className="input-wrapper">
                    <IndianRupee className="input-icon" size={16} />
                    <input
                      type="number"
                      className="form-input"
                      id="payment-amount"
                      placeholder="Enter amount"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Remark input */}
                <div className="form-group">
                  <label className="form-label">Remark</label>
                  <div className="input-wrapper">
                    <Send className="input-icon" size={16} />
                    <input
                      type="text"
                      className="form-input"
                      id="payment-remark"
                      placeholder="Add remark (optional)"
                      value={paymentRemark}
                      onChange={(e) => setPaymentRemark(e.target.value)}
                    />
                  </div>
                </div>

                {/* File upload drag drop zone */}
                <div className="form-group form-full-width">
                  <label className="form-label">Payment Proof Upload</label>
                  <div
                    className="upload-dropzone"
                    id="payment-upload-zone"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleUploadClick}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp,.ico,.doc,.docx,.xls,.xlsx"
                      style={{ display: 'none' }}
                    />
                    <UploadCloud className="upload-icon" size={32} />
                    <p className="upload-main-text">
                      {paymentFile ? `Selected: ${paymentFile.name}` : "Click to upload or drag and drop"}
                    </p>
                    <p className="upload-sub-text">PDF, PNG, JPG up to 10MB</p>
                    {paymentFile && (
                      <button
                        type="button"
                        className="btn-danger"
                        style={{ marginTop: '8px', padding: '4px 10px', fontSize: '11px' }}
                        onClick={(e) => { e.stopPropagation(); setPaymentFile(null); }}
                      >
                        Remove File
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-full-width" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button type="submit" className="btn-primary" id="save-payment-entry-btn">
                    Save Payment Entry
                    <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Transaction Ledger Table Card */}
          <div className="erp-card">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 className="section-title">Transaction Details Ledger</h2>
                <p className="section-subtitle">Realtime balance statement ledger for pending and settled invoices</p>
              </div>

              <div className="input-wrapper" style={{ width: '260px' }}>
                <Search className="input-icon" size={15} />
                <input
                  type="text"
                  className="form-input"
                  id="ledger-search-box"
                  placeholder="Search ledger..."
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  style={{ padding: '8px 10px 8px 34px' }}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="erp-table" id="ledger-data-table">
                <thead>
                  <tr>
                    <th>Party Name</th>
                    <th>Collection Date</th>
                    <th>Chq Date</th>
                    <th>Pay Style</th>
                    <th>Chq No</th>
                    <th>Bank Name</th>
                    <th>Amount</th>
                    <th>Remark</th>
                    <th>Proof</th>
                    <th>Approval</th>
                  </tr>
                </thead>
                <tbody>
                  {displayLedger.filter(el => {
                    const search = ledgerSearch.toLowerCase();
                    return (
                      (el.partyName && el.partyName.toLowerCase().includes(search)) ||
                      (el.chqNo && el.chqNo.toLowerCase().includes(search)) ||
                      (el.payStyle && el.payStyle.toLowerCase().includes(search)) ||
                      (el.chqBankName && el.chqBankName.toLowerCase().includes(search))
                    );
                  }).length > 0 ? (
                    displayLedger
                      .filter(el => {
                        const search = ledgerSearch.toLowerCase();
                        return (
                          (el.partyName && el.partyName.toLowerCase().includes(search)) ||
                          (el.chqNo && el.chqNo.toLowerCase().includes(search)) ||
                          (el.payStyle && el.payStyle.toLowerCase().includes(search)) ||
                          (el.chqBankName && el.chqBankName.toLowerCase().includes(search))
                        );
                      })
                      .map((item) => (
                        <tr key={item.id}>
                          <td><strong style={{ color: '#0f172a' }}>{item.partyName}</strong></td>
                          <td>{item.currentDate}</td>
                          <td>{item.chqDate}</td>
                          <td>
                            <span className={item.payStyle === 'CHEQUE' ? 'badge-yellow' : 'badge-green'} style={{ fontSize: '10px', padding: '2px 8px' }}>
                              {item.payStyle}
                            </span>
                          </td>
                          <td>{item.chqNo}</td>
                          <td>{item.chqBankName}</td>
                          <td className="currency-green">{formatAsINR(item.amount)}</td>
                          <td>{item.remark}</td>
                          <td>
                            {item.billImg && item.billImg !== '0' ? (
                              <a
                                href={item.billImg}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-secondary"
                                style={{ padding: '6px', color: '#0ea5e9', display: 'inline-flex', alignItems: 'center' }}
                                title="View Payment Proof"
                              >
                                <Eye size={13} />
                              </a>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '11px' }}>No Proof</span>
                            )}
                          </td>
                          <td>{item.Approval}</td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', color: '#64748b', padding: '24px', fontStyle: 'italic' }}>
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recovery Re-Schedule Card */}
          {hasPartyWiseResponse && (
            <div className="erp-card">
              <div className="section-header">
                <h2 className="section-title">Recovery Re-Schedule</h2>
                <p className="section-subtitle">Re-schedule collection visit agenda and priorities</p>
              </div>

              <form className="erp-form" onSubmit={handleConfirmProtocol}>
                <div className="form-group">
                  <label className="form-label">Next Visit Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <div className="input-wrapper">
                    <Calendar className="input-icon" size={16} />
                    <input
                      type="date"
                      className="form-input"
                      id="resched-visit-date"
                      value={reschedDate}
                      onChange={(e) => setReschedDate(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Visit Time <span style={{ color: '#ef4444' }}>*</span></label>
                  <div className="input-wrapper">
                    <Clock className="input-icon" size={16} />
                    <input
                      type="time"
                      className="form-input"
                      id="resched-visit-time"
                      value={reschedTime}
                      onChange={(e) => setReschedTime(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    className="filter-select text-white"
                    id="resched-visit-priority"
                    value={reschedPriority}
                    onChange={(e) => setReschedPriority(e.target.value)}
                    style={{ backgroundColor: '#080c14' }}
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>

                <div className="form-group form-full-width">
                  <label className="form-label">
                    Intelligence Agenda <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    id="resched-visit-agenda"
                    placeholder="e.g., Payment recovery flow..."
                    value={reschedAgenda}
                    onChange={(e) => setReschedAgenda(e.target.value)}
                    required
                    style={{ paddingLeft: '12px' }}
                  />
                </div>

                <div className="form-full-width" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button type="submit" className="btn-primary" id="confirm-protocol-btn">
                    Confirm Protocol
                    <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Schedule Protocol Details Card */}
          {/* Schedule Protocol Details Card */}
          <div className="erp-card">
            <div className="section-header">
              <div>
                <h2 className="section-title">Schedule Protocol Details</h2>
                <p className="section-subtitle">Realtime scheduled recovery visit protocols and priorities</p>
              </div>
            </div>

            <div className="table-responsive">
              <table className="erp-table" id="schedule-protocol-table">
                <thead>
                  <tr>
                    <th>Party Name</th>
                    <th>Next Visit Date</th>
                    <th>Visit Time</th>
                    <th>Priority</th>
                    <th>Intelligence Agenda / Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleList && scheduleList.length > 0 ? (
                    scheduleList.map((item, idx) => {
                      const partyName = getFieldValue(item, ['PartyName', 'AcName', 'partyName', 'Party', 'party']);
                      let nextDate = getFieldValue(item, ['NxtVisitDate', 'Date', 'NextVisitDate', 'date', 'currentDate']);
                      if (nextDate && nextDate.includes('T')) {
                        nextDate = nextDate.split('T')[0];
                      }
                      let visitTime = getFieldValue(item, ['NxtVisitTime', 'Time', 'VisitTime', 'time']);
                      if (visitTime && visitTime.includes('T')) {
                        const timePart = visitTime.split('T')[1];
                        if (timePart) {
                          visitTime = timePart.substring(0, 5);
                        }
                      }
                      const priority = getFieldValue(item, ['Priority', 'Preority', 'priority', 'preority']);
                      const remark = getFieldValue(item, ['Agenda', 'Remark', 'remark', 'Remarks', 'Remarks1', 'agenda']);

                      return (
                        <tr key={idx}>
                          <td><strong style={{ color: '#0f172a' }}>{partyName || partyFilter || ''}</strong></td>
                          <td>{nextDate}</td>
                          <td>{visitTime || '10:00 AM'}</td>
                          <td>
                            <span className={
                              (priority || '').toUpperCase() === 'CRITICAL' ? 'badge-red' :
                                (priority || '').toUpperCase() === 'HIGH' ? 'badge-orange' :
                                  (priority || '').toUpperCase() === 'LOW' ? 'badge-green' : 'badge-blue'
                            } style={{ fontSize: '10px', padding: '2px 8px' }}>
                              {priority || 'NORMAL'}
                            </span>
                          </td>
                          <td>{remark || 'No Agenda'}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', color: '#64748b', padding: '24px', fontStyle: 'italic' }}>
                        No scheduled protocols found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* ========================================================
          TAB 2: AGING ANALYSIS VIEW 
          ======================================================== */}
      {activeSubTab === 'aging' && (
        <div className="tab-view-panel" id="view-aging-analysis">

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 className="banner-h1" style={{ fontSize: '18px' }}>Aging Analysis</h2>
              <span className="badge-yellow" style={{ fontSize: '10px' }}>Live Audit</span>
            </div>


          </div>

          {/* Metric KPIs row */}
          <div className="kpi-row" id="aging-kpi-row">
            <div className="kpi-card" style={{ borderTop: '3px solid #3b82f6' }}>
              <div className="kpi-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Total Net Outstanding
                  <div className="kpi-info-container">
                    <Info size={14} color="#94a3b8" style={{ cursor: 'help' }} />
                    <div className="kpi-info-tooltip">{agingKpis.totalTooltip}</div>
                  </div>
                </div>
                <TrendingUp size={14} className="kpi-icon" />
              </div>
              <div className="kpi-val">{formatAsINR(agingKpis.total)}</div>
              <div className="kpi-sub">{agingKpis.totalCount} records</div>
            </div>

            <div className="kpi-card" style={{ borderTop: '3px solid #ef4444' }}>
              <div className="kpi-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {agingKpis.highRiskLabel}
                  <div className="kpi-info-container">
                    <Info size={14} color="#94a3b8" style={{ cursor: 'help' }} />
                    <div className="kpi-info-tooltip">{agingKpis.highRiskTooltip}</div>
                  </div>
                </div>
                <AlertTriangle size={14} className="kpi-icon" color="#ef4444" />
              </div>
              <div className="kpi-val" style={{ color: '#ef4444' }}>{formatAsINR(agingKpis.highRisk)}</div>
              <div className="kpi-sub" style={{ color: '#fca5a5' }}>{agingKpis.highRiskPct}% of total</div>
            </div>


            <div className="kpi-card" style={{ borderTop: '3px solid #22c55e' }}>
              <div className="kpi-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {agingKpis.freshLabel}
                  <div className="kpi-info-container">
                    <Info size={14} color="#94a3b8" style={{ cursor: 'help' }} />
                    <div className="kpi-info-tooltip">{agingKpis.freshTooltip}</div>
                  </div>
                </div>
                <CheckCircle2 size={14} className="kpi-icon" color="#22c55e" />
              </div>
              <div className="kpi-val" style={{ color: '#22c55e' }}>{formatAsINR(agingKpis.fresh)}</div>
              <div className="kpi-sub">{agingKpis.freshPct}% of total</div>
            </div>
          </div>

          {/* Aging Ledger Matrix Table */}
          <div className="erp-card">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 className="section-title">Aging Ledger Matrix</h2>
                <p className="section-subtitle">Row 1 = Amount (₹) | Row 2 = % of Net Balance</p>
              </div>
            </div>

            <div style={{ width: '100%' }}>
              {(() => {
                const prodGroupKey = Object.keys(agingData[0] || {}).find(k => k.toLowerCase() === 'productiongroupname' || k.toLowerCase().includes('production'));
                const filteredAgingData = (prodGroupFilter && prodGroupFilter.length > 0 && prodGroupKey)
                  ? agingData.filter(row => prodGroupFilter.includes(row[prodGroupKey]))
                  : agingData;

                if (agingLoading) {
                  return (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #f1f5f9', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
                      <div style={{ marginTop: '16px' }}>Loading Aging Data...</div>
                    </div>
                  );
                }

                if (filteredAgingData && filteredAgingData.length > 0) {
                  return (
                    <CollectionAgingTable
                      columns={(() => {
                        const allKeys = Object.keys(filteredAgingData[0]).filter(key =>
                          !['acid', 'collectionid', 'groupmstid', 'productiongroupid', 'id', 'srnumber', 'partyid', 'fyear', 'companyid'].includes(key.toLowerCase())
                        );

                        const isBucket = (k) => /^[<>\d]/.test(k.trim());
                        const extractNum = (str) => {
                          const match = str.match(/\d+/);
                          return match ? parseInt(match[0], 10) : -1;
                        };

                        const buckets = allKeys.filter(isBucket);
                        buckets.sort((a, b) => extractNum(b) - extractNum(a)); // Largest to Smallest

                        let bucketIndex = 0;
                        const finalKeys = allKeys.map(k => {
                          if (isBucket(k)) {
                            return buckets[bucketIndex++];
                          }
                          return k;
                        });

                        return finalKeys.map(key => ({
                          key: key,
                          label: key.replace(/_/g, ' '),
                          align: (typeof filteredAgingData[0][key] === 'number' || (typeof filteredAgingData[0][key] === 'string' && filteredAgingData[0][key].trim() !== '' && !isNaN(parseFloat(filteredAgingData[0][key])))) ? 'right' : 'left',
                          format: true,
                          rupees: key.toLowerCase().includes('amt') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('bal')
                        }));
                      })()}
                      data={filteredAgingData}
                      initialGroupLevels={agingGroupType ? [agingGroupType] : []}
                      onConfigureBuckets={() => setShowBucketModal(true)}
                    />
                  );
                }

                return (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                    No records found for the selected criteria.
                  </div>
                );
              })()}
            </div>
          </div>

          {/* split visual charts and critical alert units */}
          <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: '1fr 1fr' }}>
            {/* Recharts Outstanding distribution */}
            <div className="erp-card">
              <div className="section-header">
                <h2 className="section-title">Outstanding Distribution</h2>
                <p className="section-subtitle">Bucket-wise breakdown of total net outstanding</p>
              </div>

              <div style={{ width: '100%', height: '300px' }}>
                <ResponsiveContainer>
                  <BarChart data={dynamicAgingChartData} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" tickLine={false} fontSize={12} />
                    <YAxis stroke="#64748b" tickLine={false} fontSize={12} tickFormatter={(val) => `₹${(val / 100000).toFixed(0)}L`} />
                    <RechartsTooltip
                      cursor={{ fill: 'rgba(0,0,0,0.02)' }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div style={{ backgroundColor: '#ffffff', border: '1px solid #cbd5e1', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{payload[0].payload.name}</p>
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: 'bold', color: '#0f172a' }}>
                                {formatAsINR(payload[0].value)}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {dynamicAgingChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Critical Dues Units */}
            <div className="erp-card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 className="section-title">Critical Units</h2>
                  {topCriticalUnits.length > 0 && (
                    <p className="section-subtitle">Top outstanding dues by Collection Name for {topCriticalUnits[0].bucketName}</p>
                  )}
                </div>
                <span className="badge-red" style={{ fontSize: '10px' }}>High Priority</span>
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '4px' }} className="fas-pmc-wrap">
                {topCriticalUnits.map((unit, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #1c2638', paddingBottom: '8px' }}>
                    <div>
                      <strong style={{ color: '#ffffff', textTransform: 'uppercase', fontSize: '12px' }}>{unit.name}</strong>
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>{unit.group}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '12px' }}>{formatAsINR(unit.amount)}</div>
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>{unit.percentage}% of total</div>
                    </div>
                  </div>
                ))}
                {topCriticalUnits.length === 0 && (
                  <div style={{ color: '#64748b', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>No critical units found</div>
                )}
              </div>
            </div>
          </div>

        </div>
      )}

      {activeSubTab === 'grey' && (
        <div className="tab-view-panel" id="view-grey-balance">

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 className="banner-h1" style={{ fontSize: '18px' }}>Grey Balance Monitor</h2>

          </div>

          {/* Metric KPIs Row */}
          <div className="kpi-row" style={{ marginBottom: '24px' }}>
            <div className="kpi-card" style={{ borderLeft: '4px solid #0ea5e9' }}>
              <div className="kpi-top">Total Grey Balance Meters</div>
              <div className="kpi-val">{formatAsINR1(partiesList.reduce((sum, item) => sum + (Number(item.GryBal) || 0), 0))}</div>
            </div>

            <div className="kpi-card" style={{ borderLeft: '4px solid #22c55e' }}>
              <div className="kpi-top">Total Grey Balance Amount</div>
              <div className="kpi-val">{formatAsINR(partiesList.reduce((sum, item) => sum + (Number(item.Bal) || 0), 0))}</div>
            </div>
          </div>

                    {/* split checklist and Line Chart */}
          <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: '1fr 1fr' }}>

            {/* Meter Checklist */}
            <div className="erp-card">
              <div className="section-header">
                <h2 className="section-title">Meter-wise Grey Balance</h2>
                <p className="section-subtitle">Realtime balance values and warning statuses</p>
              </div>

              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="erp-table" id="grey-balance-table">
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', top: 0, backgroundColor: '#080c14', zIndex: 1 }}>Party Name</th>
                      <th style={{ position: 'sticky', top: 0, backgroundColor: '#080c14', zIndex: 1 }}>Production Group</th>
                      <th style={{ position: 'sticky', top: 0, backgroundColor: '#080c14', zIndex: 1 }}>City</th>
                      <th style={{ position: 'sticky', top: 0, backgroundColor: '#080c14', zIndex: 1, textAlign: 'right' }}>Balance</th>
                      <th style={{ position: 'sticky', top: 0, backgroundColor: '#080c14', zIndex: 1, textAlign: 'right', color: '#0ea5e9' }}>Grey Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partiesList && partiesList.length > 0 ? (
                      partiesList.map((item, index) => (
                        <tr key={item.acid || index}>
                          <td>{item.PartyName}</td>
                          <td>{item.ProductionGroupMstName || '-'}</td>
                          <td>{item.CityName || '-'}</td>
                          <td style={{ textAlign: 'right' }}>{formatAsINR(item.Bal)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#0ea5e9' }}>{formatAsINR1(item.GryBal)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="empty-state" style={{ textAlign: 'center', padding: '20px' }}>
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recharts Area trend chart */}
            <div className="erp-card">
              <div className="section-header">
                <h2 className="section-title">Grey Balance Trend</h2>
                <p className="section-subtitle">Party-wise grey balance amounts</p>
              </div>

              <div style={{ width: '100%', height: '280px' }}>
                <ResponsiveContainer>
                  <AreaChart data={partiesList.map(item => ({
                    name: item.ShortAcName || (item.PartyName ? item.PartyName.substring(0, 10) : ''),
                    amount: Number(item.GryBal) || 0
                  }))} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" tickLine={false} />
                    <YAxis stroke="#64748b" tickLine={false} tickFormatter={(val) => `₹${(val / 1000).toFixed(0)}k`} />
                    <RechartsTooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div style={{ backgroundColor: '#ffffff', border: '1px solid #cbd5e1', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{payload[0].payload.name}</p>
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: 'bold', color: '#0f172a' }}>
                                {formatAsINR(payload[0].value)}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area type="monotone" dataKey="amount" stroke="#0ea5e9" strokeWidth={3} fillOpacity={1} fill="url(#colorAmt)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>

          {/* Top 5 and Last 5 Row */}
          {(() => {
            const sortedByGryBal = [...(partiesList || [])].sort((a, b) => (Number(b.GryBal) || 0) - (Number(a.GryBal) || 0));
            const top10 = sortedByGryBal.slice(0, 10);

            return (
              <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: '1fr', marginBottom: '24px' }}>
                <div className="erp-card">
                  <div className="section-header">
                    <h2 className="section-title">Top 10 (Highest Grey Balance)</h2>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                    {top10.map((item, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 16px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        borderLeft: `4px solid ${['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6'][idx] || '#0ea5e9'}`,
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}>
                        <div>
                          <div style={{ color: '#0f172a', fontWeight: 'bold', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              fontSize: '11px',
                              fontWeight: 'bold'
                            }}>
                              {idx + 1}
                            </span>
                            {item.PartyName}
                          </div>
                          <div style={{ color: '#64748b', fontSize: '11px', marginTop: '6px', paddingLeft: '28px' }}>
                            {item.ProductionGroupMstName || 'No Group'} • {item.CityName || 'No City'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '16px', letterSpacing: '0.5px' }}>
                            {formatAsINR1(item.GryBal)}
                          </div>
                          <div style={{ color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginTop: '2px', letterSpacing: '0.5px' }}>
                            Grey Bal
                          </div>
                        </div>
                      </div>
                    ))}
                    {top10.length === 0 && (
                      <div className="empty-state" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>No data available</div>
                    )}
                  </div>
                </div>


              </div>
            );
          })()}



        </div>
      )}

      {/* ========================================================
          TAB 4: PAYMENT HISTORY VIEW (Master-Detail Split)
          ======================================================== */}
      {activeSubTab === 'history' && (
        <div className="tab-view-panel" id="view-payment-history">
          {/* Transaction Ledger Table Card */}
          <div className="erp-card">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 className="section-title">Transaction Details Ledger</h2>
                <p className="section-subtitle">Chronological collection history log by verified customers</p>
              </div>

              <div className="input-wrapper" style={{ width: '260px' }}>
                <Search className="input-icon" size={15} />
                <input
                  type="text"
                  className="form-input"
                  id="history-ledger-search-box"
                  placeholder="Search history ledger..."
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  style={{ padding: '8px 10px 8px 34px' }}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="erp-table" id="history-ledger-data-table">
                <thead>
                  <tr>
                    <th>Party Name</th>
                    <th>Collection Date</th>
                    <th>Chq Date</th>
                    <th>Pay Style</th>
                    <th>Chq No</th>
                    <th>Bank Name</th>
                    <th>Amount</th>
                    <th>Remark</th>
                    <th>Proof</th>
                  </tr>
                </thead>
                <tbody>
                  {displayLedger.filter(el => {
                    const search = ledgerSearch.toLowerCase();
                    return (
                      (el.partyName && el.partyName.toLowerCase().includes(search)) ||
                      (el.chqNo && el.chqNo.toLowerCase().includes(search)) ||
                      (el.payStyle && el.payStyle.toLowerCase().includes(search)) ||
                      (el.chqBankName && el.chqBankName.toLowerCase().includes(search))
                    );
                  }).length > 0 ? (
                    displayLedger
                      .filter(el => {
                        const search = ledgerSearch.toLowerCase();
                        return (
                          (el.partyName && el.partyName.toLowerCase().includes(search)) ||
                          (el.chqNo && el.chqNo.toLowerCase().includes(search)) ||
                          (el.payStyle && el.payStyle.toLowerCase().includes(search)) ||
                          (el.chqBankName && el.chqBankName.toLowerCase().includes(search))
                        );
                      })
                      .map((item) => (
                        <tr key={item.id}>
                          <td><strong style={{ color: '#0f172a' }}>{item.partyName}</strong></td>
                          <td>{item.currentDate}</td>
                          <td>{item.chqDate}</td>
                          <td>
                            <span className={item.payStyle === 'CHEQUE' ? 'badge-yellow' : 'badge-green'} style={{ fontSize: '10px', padding: '2px 8px' }}>
                              {item.payStyle}
                            </span>
                          </td>
                          <td>{item.chqNo}</td>
                          <td>{item.chqBankName}</td>
                          <td className="currency-green">{formatAsINR(item.amount)}</td>
                          <td>{item.remark}</td>
                          <td>
                            {item.billImg && item.billImg !== '0' ? (
                              <a
                                href={item.billImg}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-secondary"
                                style={{ padding: '6px', color: '#0ea5e9', display: 'inline-flex', alignItems: 'center' }}
                                title="View Payment Proof"
                              >
                                <Eye size={13} />
                              </a>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '11px' }}>No Proof</span>
                            )}
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', color: '#64748b', padding: '24px', fontStyle: 'italic' }}>
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================
          TAB 5: SCHEDULE VISIT VIEW
          ======================================================== */}
      {activeSubTab === 'schedule' && (
        <div className="tab-view-panel" id="view-schedule-visit">

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2 className="banner-h1" style={{ fontSize: '18px' }}>Schedule Visit</h2>
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Plan and track customer visit schedules</p>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                className="filter-select text-white"
                value={visitFilter}
                onChange={(e) => setVisitFilter(e.target.value)}
                style={{ width: '120px', padding: '6px 10px', height: '36px', backgroundColor: '#121824', borderColor: '#1c2638', color: '#ffffff' }}
              >
                <option value="All">All Visits</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
                <option value="LOW">Low</option>
              </select>

              {/* Toggle display mode button group style */}
              <div className="toggle-view-group">
                <button
                  className={`toggle-view-btn ${scheduleViewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setScheduleViewMode('list')}
                >
                  <List size={14} />
                  List
                </button>
                <button
                  className={`toggle-view-btn ${scheduleViewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setScheduleViewMode('calendar')}
                >
                  <Calendar size={14} />
                  Calendar
                </button>
              </div>

              <button className="btn-primary" style={{ height: '36px', padding: '0 16px', borderRadius: '20px' }} onClick={() => setShowScheduleModal(true)}>
                <Plus size={16} />
                Schedule Visit
              </button>
            </div>
          </div>

          {scheduleViewMode === 'list' ? (
            /* Chronological List of visit logs (List Mode) */
            <div className="erp-card">
              <div className="section-header">
                <h2 className="section-title">PAST VISITS ({["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"][calendarMonth]})</h2>
                <p className="section-subtitle">Realtime agent logs and conversation outcomes</p>
              </div>

              <div className="schedule-scroll-area">
                {derivedVisitSchedules
                  .filter(v => visitFilter === 'All' || v.status === visitFilter)
                  .map((item) => (
                    <div className="schedule-item" key={item.id}>
                      <div className="schedule-left">
                        <div className="schedule-date-block">
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{item.date.split(' ')[0]}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>{item.date.split(' ')[1]}</div>
                        </div>

                        <div className="schedule-party-info">
                          <div className="schedule-party-name">{item.name}</div>
                          <div className="schedule-party-meta">
                            {item.time && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={11} /> {item.time}</span>}
                            {item.agent && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><User size={11} /> {item.agent}</span>}
                            {item.area && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MapPin size={11} /> {item.area}</span>}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                        <div style={{ fontSize: '12px', color: '#0f172a', fontWeight: '600' }}>
                          {item.outcome}
                        </div>

                        <span className={
                          (item.priority || item.status || '').toUpperCase() === 'CRITICAL' ? 'badge-red' :
                            (item.priority || item.status || '').toUpperCase() === 'HIGH' ? 'badge-orange' :
                              (item.priority || item.status || '').toUpperCase() === 'LOW' ? 'badge-green' : 'badge-blue'
                        } style={{ fontSize: '9px', width: '90px', padding: '4px', textAlign: 'center' }}>
                          {item.priority || item.status || 'NORMAL'}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            /* Stunning Monthly Calendar View & Sidebar Detail Split Grid (Calendar Mode) */
            (() => {
              const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
              const firstDayIndex = new Date(calendarYear, calendarMonth, 1).getDay();
              const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

              const handlePrevMonth = () => {
                if (calendarMonth === 0) {
                  setCalendarMonth(11);
                  setCalendarYear(prev => prev - 1);
                } else {
                  setCalendarMonth(prev => prev - 1);
                }
                setSelectedCalendarDay(1);
              };

              const handleNextMonth = () => {
                if (calendarMonth === 11) {
                  setCalendarMonth(0);
                  setCalendarYear(prev => prev + 1);
                } else {
                  setCalendarMonth(prev => prev + 1);
                }
                setSelectedCalendarDay(1);
              };

              return (
                <div className="calendar-layout-grid">
                  {/* Left Calendar Grid */}
                  <div className="calendar-main-card">
                    <div className="calendar-header-row">
                      <div className="calendar-month-title">
                        <button className="calendar-nav-btn" onClick={handlePrevMonth}>&lt;</button>
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{monthNames[calendarMonth]} {calendarYear}</span>
                        <button className="calendar-nav-btn" onClick={handleNextMonth}>&gt;</button>
                      </div>

                      <div className="calendar-legends">
                        <div className="calendar-legend-item">
                          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#dc2626' }}></span>
                          Critical
                        </div>
                        <div className="calendar-legend-item">
                          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ea580c' }}></span>
                          High
                        </div>
                        <div className="calendar-legend-item">
                          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#0284c7' }}></span>
                          Normal
                        </div>
                        <div className="calendar-legend-item">
                          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#16a34a' }}></span>
                          Low
                        </div>
                      </div>
                    </div>

                    {/* Weekdays header */}
                    <div className="calendar-grid-header">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                        <div className="calendar-weekday-label" key={d}>{d}</div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="calendar-days-grid">
                      {/* Dynamic empty cells based on starting weekday */}
                      {[...Array(firstDayIndex)].map((_, i) => (
                        <div className="calendar-day-cell empty" key={`empty-${i}`}></div>
                      ))}

                      {/* Days 1 to daysInMonth */}
                      {[...Array(daysInMonth)].map((_, i) => {
                        const day = i + 1;
                        const isSelected = day === selectedCalendarDay;

                        // Dynamic check for Today's Date
                        const realToday = new Date();
                        const isToday = day === realToday.getDate() && calendarMonth === realToday.getMonth() && calendarYear === realToday.getFullYear();

                        const dayEvents = derivedCalendarEvents[day] || [];
                        const filteredEvents = dayEvents.filter(e => visitFilter === 'All' || e.status === visitFilter);

                        return (
                          <div
                            className={`calendar-day-cell ${isSelected ? 'selected' : ''}`}
                            key={day}
                            onClick={() => setSelectedCalendarDay(day)}
                          >
                            <div className="calendar-day-num-row">
                              <span className="calendar-day-number">{day}</span>
                              {isToday && <span className="calendar-today-badge">TODAY</span>}
                            </div>

                            {/* Events list preview */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', overflow: 'hidden' }}>
                              {filteredEvents.slice(0, 2).map(evt => (
                                <div
                                  key={evt.id}
                                  className={`calendar-event-capsule ${(evt.priority || evt.status || 'NORMAL').toLowerCase()}`}
                                >
                                  <span style={{ display: 'inline-block', width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'currentColor' }}></span>
                                  {evt.name}
                                </div>
                              ))}
                              {filteredEvents.length > 2 && (
                                <span className="calendar-more-events">+{filteredEvents.length - 2} more</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Sidebar Detail List */}
                  <div className="calendar-sidebar-card">
                    {(() => {
                      const selectedDateObj = new Date(calendarYear, calendarMonth, selectedCalendarDay);
                      const dayOfWeekNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                      const dayName = dayOfWeekNames[selectedDateObj.getDay()];
                      const activeEvents = (derivedCalendarEvents[selectedCalendarDay] || []).filter(e => visitFilter === 'All' || e.status === visitFilter);

                      const realToday = new Date();
                      const realTodayDay = realToday.getDate();
                      const realTodayMonth = realToday.getMonth();
                      const realTodayYear = realToday.getFullYear();

                      let dateStatus = "PAST";
                      const activeSelectedDate = new Date(calendarYear, calendarMonth, selectedCalendarDay);
                      const activeTodayDate = new Date(realTodayYear, realTodayMonth, realTodayDay);

                      if (activeSelectedDate.toDateString() === activeTodayDate.toDateString()) {
                        dateStatus = "TODAY";
                      } else if (activeSelectedDate > activeTodayDate) {
                        dateStatus = "UPCOMING";
                      }

                      return (
                        <>
                          <div className="calendar-sidebar-header">
                            <div className="calendar-sidebar-meta-status">
                              <span style={{
                                display: 'inline-block',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: dateStatus === 'TODAY' ? '#38bdf8' : dateStatus === 'UPCOMING' ? '#22c55e' : '#64748b'
                              }}></span>
                              {dateStatus}
                            </div>
                            <h3 className="calendar-sidebar-title">{dayName}, {selectedCalendarDay} {monthNames[calendarMonth]}</h3>
                            <p className="calendar-sidebar-subtitle">{activeEvents.length} visits scheduled</p>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {activeEvents.map(evt => (
                              <div className="calendar-sidebar-item" key={evt.id}>
                                <div className="calendar-sidebar-item-top">
                                  <span className="calendar-sidebar-party-name">{evt.name}</span>
                                  <span className={`calendar-sidebar-badge ${(evt.priority || evt.status || 'NORMAL').toLowerCase()}`}>
                                    {evt.priority || evt.status || 'NORMAL'}
                                  </span>
                                </div>

                                <div className="calendar-sidebar-item-meta">
                                  {evt.time && (
                                    <div className="calendar-sidebar-meta-col">
                                      <Clock size={11} />
                                      {evt.time}
                                    </div>
                                  )}
                                  {evt.agent && (
                                    <div className="calendar-sidebar-meta-col">
                                      <User size={11} />
                                      {evt.agent}
                                    </div>
                                  )}
                                  {evt.area && (
                                    <div className="calendar-sidebar-meta-col">
                                      <MapPin size={11} />
                                      {evt.area}
                                    </div>
                                  )}
                                </div>

                                {evt.outcome && (
                                  <div className="calendar-sidebar-item-remarks">
                                    {evt.outcome}
                                  </div>
                                )}
                              </div>
                            ))}

                            {activeEvents.length === 0 && (
                              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                                <Calendar size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                                <p style={{ margin: 0, fontSize: '13px' }}>No visits scheduled for this date.</p>
                                <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#475569' }}>Click "+ Schedule Visit" above to add one.</p>
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            })()
          )}

        </div>
      )}

      {/* ========================================================
          TAB 7: GET GPS LAT/LOG MODULE (Standalone Feature)
          ======================================================== */}
      {activeSubTab === 'latlog' && (
        <div className="tab-view-panel" id="view-latlog-tracking">
          {/* Inner Sub-navigation Mode Selection */}
          <div className="latlog-inner-tabs" style={{ display: 'flex', gap: '12px', marginBottom: '20px', borderBottom: '1px solid #cbd5e1', paddingBottom: '10px' }}>
            <button
              className={`subtab-btn ${latlogSubTab === 'single' ? 'active' : ''}`}
              onClick={() => { setLatlogSubTab('single'); addToast("Switched to Update GPS mode"); }}
              style={{ borderRadius: '20px', fontSize: '12px', padding: '8px 16px' }}
            >
              <MapPin size={14} style={{ marginRight: '6px' }} />
              Single Tracker & GPS Update
            </button>
            <button
              className={`subtab-btn ${latlogSubTab === 'multi' ? 'active' : ''}`}
              onClick={() => { setLatlogSubTab('multi'); addToast("Switched to Group Map View"); }}
              style={{ borderRadius: '20px', fontSize: '12px', padding: '8px 16px' }}
            >
              <Route size={14} style={{ marginRight: '6px' }} />
              Group Map View (Multi-Select)
            </button>
          </div>

          <div className="latlog-split-container">

            {/* Left Sidebar List of Parties */}
            <div className="latlog-list-card">
              <div className="section-header" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 className="section-title" style={{ fontSize: '13px' }}>Customer Beat List</h3>
                  <p className="section-subtitle" style={{ margin: 0 }}>
                    {latlogSubTab === 'single' ? "Select a customer to view or update GPS logs" : "Check customers to view them together on map"}
                  </p>
                </div>
              </div>

              {latlogSubTab === 'multi' && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <button
                    className="btn-secondary"
                    style={{ flex: 1, fontSize: '11px', padding: '6px 12px', borderRadius: '6px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={() => {
                      setParties(prev => prev.map(p => ({ ...p, selected: true })));
                    }}
                  >
                    Select All
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ flex: 1, fontSize: '11px', padding: '6px 12px', borderRadius: '6px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={() => {
                      setParties(prev => prev.map(p => ({ ...p, selected: false })));
                    }}
                  >
                    Clear All
                  </button>
                </div>
              )}

              {/* Search bar */}
              <div className="latlog-search-wrapper">
                <Search className="input-icon" size={15} style={{ left: '12px', color: '#64748b' }} />
                <input
                  type="text"
                  className="latlog-search-input"
                  placeholder="Search customer party or area..."
                  value={latlogSearch}
                  onChange={(e) => setLatlogSearch(e.target.value)}
                />
              </div>

              {/* Party Scroll List */}
              <div className="latlog-scroll-area">
                {parties
                  .filter(p => p.name.toLowerCase().includes(latlogSearch.toLowerCase()) || p.area.toLowerCase().includes(latlogSearch.toLowerCase()))
                  .map(party => {
                    const isCurrent = party.id === selectedPartyId;
                    return (
                      <div
                        key={party.id}
                        className={`latlog-party-item ${isCurrent && latlogSubTab === 'single' ? 'active' : ''}`}
                        onClick={() => {
                          if (latlogSubTab === 'single') {
                            setSelectedPartyId(party.id);
                            setTempGPS(null); // Reset preview on new selection
                            addToast(`Selected ${party.name}`);
                          } else {
                            togglePartySelection(party.id);
                            addToast(`${party.selected ? 'Removed' : 'Added'} ${party.name} on map view`);
                          }
                        }}
                      >
                        {/* Radio button for Single mode, Checkbox for Multi mode */}
                        {latlogSubTab === 'single' ? (
                          <input
                            type="radio"
                            name="latlog-single-select"
                            className="latlog-party-checkbox"
                            style={{ borderRadius: '50%' }}
                            checked={party.id === selectedPartyId}
                            onChange={(e) => {
                              setSelectedPartyId(party.id);
                              setTempGPS(null); // Reset preview on new selection
                              addToast(`Selected ${party.name}`);
                            }}
                          />
                        ) : (
                          <input
                            type="checkbox"
                            className="latlog-party-checkbox"
                            checked={party.selected}
                            onChange={(e) => {
                              e.stopPropagation();
                              togglePartySelection(party.id);
                              addToast(`${party.selected ? 'Removed' : 'Added'} ${party.name} on map view`);
                            }}
                          />
                        )}

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="latlog-party-name">{party.name}</span>
                            <span className={party.status === 'Active' ? 'badge-green' : 'badge-yellow'} style={{ fontSize: '9px', padding: '1px 6px' }}>
                              {party.status}
                            </span>
                          </div>
                          <div className="latlog-party-area">{party.area}</div>
                          <div className="latlog-party-coords">
                            {party.lat && party.lng ? (
                              <>
                                <span>Lat: {party.lat.toFixed(6)}</span>
                                <span>Lng: {party.lng.toFixed(6)}</span>
                              </>
                            ) : (
                              <span style={{ color: '#64748b', fontStyle: 'italic' }}>GPS Not Set</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Right Map Canvas Container */}
            <div className="latlog-map-card">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <h3 className="section-title" style={{ fontSize: '13px' }}>Customer GPS Coordinates</h3>
                  <p className="section-subtitle">
                    {latlogSubTab === 'single'
                      ? `Viewing coordinates for ${parties.find(p => p.id === selectedPartyId)?.name}`
                      : `Displaying ${parties.filter(p => p.selected).length} selected customers simultaneously`}
                  </p>
                </div>
              </div>

              {/* Leaflet Real Map Canvas */}
              <div className="latlog-map-viewbox" style={{ overflow: 'hidden' }}>
                <div id="leaflet-map-container" style={{ width: '100%', height: '100%', minHeight: '380px', zIndex: 1 }}></div>
              </div>

              {/* GPS Lat/Log details and fetch controls */}
              {latlogSubTab === 'single' ? (
                (() => {
                  const focusedParty = parties.find(p => p.id === selectedPartyId);
                  return (
                    <div className="latlog-action-bar">
                      {focusedParty ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div style={{ textAlign: 'left' }}>
                              <strong style={{ color: '#0f172a', fontSize: '13px' }}>{focusedParty.name}</strong>
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                Saved Position: <span style={{ fontFamily: 'monospace', color: '#64748b', fontWeight: 'bold' }}>
                                  {focusedParty.lat && focusedParty.lng
                                    ? `${focusedParty.lat.toFixed(6)}° N, ${focusedParty.lng.toFixed(6)}° E`
                                    : 'Not Set'}
                                </span>
                              </div>
                            </div>

                            <button
                              className="btn-primary"
                              onClick={() => handleUpdateGPS(focusedParty.id)}
                              disabled={gpsLoading}
                              style={{ padding: '8px 16px', borderRadius: '20px', fontSize: '12px' }}
                            >
                              {gpsLoading ? (
                                <>
                                  <RefreshCw className="kpi-icon" size={14} style={{ animation: 'spin 1.5s linear infinite' }} />
                                  Fetching...
                                </>
                              ) : (
                                <>
                                  <RefreshCw size={14} />
                                  Get Current GPS Location
                                </>
                              )}
                            </button>
                          </div>

                          {/* Show temp preview coordinates and Save/Update button */}
                          {tempGPS && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', borderTop: '1px dashed #cbd5e1', paddingTop: '12px', marginTop: '4px' }}>
                              <div style={{ textAlign: 'left' }}>
                                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📍 New GPS Preview</span>
                                <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#16a34a', fontWeight: 'bold', marginTop: '2px' }}>
                                  {tempGPS.lat.toFixed(6)}° N, {tempGPS.lng.toFixed(6)}° E
                                </div>
                              </div>

                              <button
                                className="btn-primary"
                                onClick={() => handleSaveGPS(focusedParty.id)}
                                style={{ padding: '8px 24px', borderRadius: '20px', fontSize: '12px', backgroundColor: '#22c55e', border: 'none', color: '#ffffff', boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)' }}
                              >
                                Update Coordinates
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ color: '#64748b', fontSize: '12px', fontStyle: 'italic' }}>Please choose a customer from the side menu.</div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div style={{ marginTop: '14px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '11px', color: '#64748b', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <AlertCircle size={15} color="#0ea5e9" />
                  <span>Showing real-time route trajectory mapping for multi-selected customer beat areas. Connects points based on selected items.</span>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ========================================================
          ROUTE MANAGEMENT TAB
          ======================================================== */}
      {activeSubTab === 'route' && (
        <div className="tab-view-panel" id="view-route-management">
          <div className="erp-card" style={{ padding: '0', display: 'flex', flexDirection: 'column', height: '100%', minHeight: '600px', overflow: 'hidden' }}>
            
            <div className="section-header" style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', margin: 0, backgroundColor: '#f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 className="section-title" style={{ fontSize: '16px' }}>Route Optimization Manager</h2>
                  <p className="section-subtitle" style={{ margin: '4px 0 0 0' }}>Select parties from the left to build a custom collector route</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button 
                    className="btn-secondary" 
                    onClick={handleDownloadRoutePDF}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#ffffff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                  >
                    <Download size={16} />
                    Download PDF
                  </button>
                  <button 
                    className="btn-primary" 
                    onClick={handleOpenInMap}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#10b981', borderColor: '#10b981' }}
                  >
                    <MapPin size={16} />
                    Open Route in Google Maps
                  </button>
                </div>
              </div>
            </div>

            <div className="latlog-split-container" style={{ padding: '24px', flex: 1, overflow: 'hidden' }}>
              {/* Left Sidebar: Party List */}
              <div className="latlog-list-card" style={{ padding: '0', border: 'none', boxShadow: 'none' }}>
                <div style={{ padding: '0 0 12px 0' }}>
                  <div className="latlog-search-wrapper" style={{ margin: 0 }}>
                    <Search className="input-icon" size={15} style={{ left: '12px', color: '#64748b' }} />
                    <input
                      type="text"
                      className="latlog-search-input"
                      placeholder="Search parties..."
                      value={routeSearch}
                      onChange={(e) => setRouteSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="latlog-scroll-area">
                  {(partiesList || [])
                    .filter(p => (p.PartyName || '').toLowerCase().includes(routeSearch.toLowerCase()) || (p.CityName || '').toLowerCase().includes(routeSearch.toLowerCase()))
                    .sort((a, b) => (Number(b.Bal || b.TotalOs || 0) || 0) - (Number(a.Bal || a.TotalOs || 0) || 0))
                    .map(party => {
                      const pId = party.acid || party.AcID || party.id;
                      const inRoute = routeSequence.some(p => (p.acid || p.AcID || p.id) === pId);
                      return (
                        <div
                          key={pId}
                          className={`latlog-party-item ${inRoute ? 'active' : ''}`}
                          onClick={() => {
                            if (inRoute) {
                              handleRemoveFromRoute(pId);
                            } else {
                              handleAddToRoute(party);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            className="latlog-party-checkbox"
                            checked={inRoute}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (inRoute) {
                                handleRemoveFromRoute(pId);
                              } else {
                                handleAddToRoute(party);
                              }
                            }}
                          />

                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span className="latlog-party-name">{party.PartyName}</span>
                              <span className="badge-green" style={{ fontSize: '9px', padding: '1px 6px' }}>
                                Active
                              </span>
                            </div>
                            <div className="latlog-party-area">{party.CityName || 'Unknown City'}</div>
                            <div className="latlog-party-coords">
                              {party.Latitude || party.Lat || party.lat ? (
                                <>
                                  <span>Lat: {Number(party.Latitude || party.Lat || party.lat).toFixed(6)}</span>
                                  <span>Lng: {Number(party.Longitude || party.Lng || party.lng).toFixed(6)}</span>
                                </>
                              ) : (
                                <span style={{ color: '#64748b', fontStyle: 'italic' }}>GPS Not Set</span>
                              )}
                            </div>
                            <div className="latlog-party-coords" style={{ marginTop: '2px' }}>
                              <span>Bal: <strong style={{ color: '#ef4444', fontSize: '11px' }}>{typeof formatAsINR1 === 'function' ? formatAsINR1(party.Bal || party.TotalOs || 0) : `₹${(party.Bal || party.TotalOs || 0).toLocaleString('en-IN')}`}</strong></span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Right Sidebar: Route Sequence */}
              <div className="latlog-list-card" style={{ backgroundColor: '#f1f5f9', padding: '0', border: 'none', boxShadow: 'none' }}>
                <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
                  {routeSequence.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                      <Route size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                      <p style={{ fontSize: '16px', margin: 0 }}>No parties added to route yet.</p>
                      <p style={{ fontSize: '13px', marginTop: '8px' }}>Select parties from the left list to start building your route.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '600px', margin: '0 auto' }}>
                      {routeSequence.map((party, index) => (
                        <div 
                          key={party.acid || party.AcID || party.id || index}
                          draggable
                          onDragStart={(e) => handleDragStartRoute(e, index)}
                          onDragEnter={(e) => handleDragEnterRoute(e, index)}
                          onDragEnd={handleDragEndRoute}
                          onDragOver={handleDragOverRoute}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '16px', 
                            backgroundColor: '#ffffff', 
                            borderRadius: '12px', 
                            boxShadow: draggedItemIndex === index ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e2e8f0',
                            cursor: 'grab',
                            opacity: draggedItemIndex === index ? 0.5 : 1,
                            transform: draggedItemIndex === index ? 'scale(1.02)' : 'scale(1)',
                            transition: 'transform 0.1s, box-shadow 0.1s'
                          }}
                        >
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#0f172a', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px', marginRight: '16px', flexShrink: 0 }}>
                            {index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#0f172a' }}>{party.PartyName}</div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'flex', gap: '12px' }}>
                              <span><MapPin size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-2px' }} />{party.CityName || 'Unknown'}</span>
                              {(!party.Latitude && !party.Lat && !party.lat) && <span style={{ color: '#ef4444' }}>No GPS Data</span>}
                            </div>
                          </div>
                          <button 
                            onClick={() => handleRemoveFromRoute(party.acid || party.AcID || party.id)}
                            style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background-color 0.2s' }}
                            title="Remove"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {['area', 'adv'].includes(activeSubTab) && (
        <div className="tab-view-panel" id="view-placeholder-module">
          <div className="erp-card" style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: 'rgba(14, 165, 233, 0.1)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 24px'
            }}>
              <Sparkles size={48} color="#0ea5e9" />
            </div>

            <h2 className="banner-h1" style={{ fontSize: '24px', marginBottom: '10px' }}>
              {activeSubTab === 'area' && "Area Wise Outstanding Summary"}
              {activeSubTab === 'adv' && "Advanced Financial Intelligence"}
            </h2>

            <p style={{ color: '#64748b', maxWidth: '520px', margin: '0 auto 28px', fontSize: '14px', lineHeight: '22px' }}>
              This audit and automation module is currently active. The dashboard can load and export structured accounting records matching this module path dynamically.
            </p>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
              <button className="btn-primary" onClick={() => addToast("Report compiled dynamically!", "success")}>
                Generate Report
              </button>
              <button className="btn-secondary" onClick={() => setActiveSubTab('daily')}>
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interactive Schedule Visit Modal Form */}
      {showBucketModal && (
        <div className="bucket-modal-overlay" style={{ zIndex: 999999 }} onClick={() => setShowBucketModal(false)}>
          <div className="bucket-modal-content" onClick={e => e.stopPropagation()}>
            <div className="bucket-modal-header">
              <div>
                <h3 style={{ color: '#0f172a', fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Settings size={18} color="#eab308" />
                  Configure Custom Aging Day Buckets
                </h3>
                <p style={{ color: '#64748b', fontSize: '12px', margin: '6px 0 0 0' }}>
                  Define outstanding invoice ranges matching your ledger filters
                </p>
              </div>
              <button style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }} onClick={() => setShowBucketModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="bucket-modal-body">
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: '600' }}>
                  Bucket Interval (Difference in Days)
                </label>
                <input
                  type="number"
                  className="bucket-input"
                  style={{ width: '200px', fontSize: '14px', padding: '10px 12px' }}
                  value={bucketInterval}
                  onChange={(e) => setBucketInterval(parseInt(e.target.value) || 0)}
                  min="1"
                />
              </div>

              <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '12px', fontWeight: '600' }}>
                Generated Ranges Preview (Total 5 Ranges)
              </label>
              <table className="bucket-table">
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Range #</th>
                    <th style={{ width: '60%' }}>Calculated Days Range</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map((num) => {
                    const min = num === 1 ? 0 : (num - 1) * bucketInterval + 1;
                    const max = num === 5 ? null : num * bucketInterval;
                    return (
                      <tr key={num}>
                        <td style={{ color: '#0f172a', fontWeight: '500' }}>Range {num}</td>
                        <td style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                          {max === null ? `${min} & Above` : `${min} to ${max} Days`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="bucket-modal-footer">
              <button
                style={{ background: 'transparent', border: 'none', color: '#0f172a', padding: '10px 20px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer' }}
                onClick={() => setShowBucketModal(false)}
              >
                Cancel
              </button>
              <button
                style={{ background: '#3b82f6', border: 'none', color: '#ffffff', padding: '10px 24px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                onClick={() => {
                  setShowBucketModal(false);
                  dispatch(CollectionAging({ bucketInterval }));
                  addToast(`Aging ranges updated with ${bucketInterval} days interval!`);
                }}
              >
                <Check size={16} />
                Apply Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <div className="schedule-modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="schedule-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="schedule-modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="schedule-modal-title">
                  <Calendar size={18} color="#38bdf8" />
                  Schedule New Customer Visit
                </h3>
                <button className="schedule-modal-close" onClick={() => setShowScheduleModal(false)}>×</button>
              </div>
              <p className="schedule-modal-subtitle">
                Fill in details to assign a collector beat visit
              </p>
            </div>

            <form onSubmit={handleAddNewVisit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Field 2: Customer Select */}
              <div className="schedule-form-group">
                <label className="schedule-form-label">
                  CUSTOMER / PARTY NAME *
                </label>
                <div className="schedule-input-wrapper" style={{ marginBottom: '8px' }}>
                  <Briefcase className="schedule-input-icon" size={16} style={{ zIndex: 10 }} />
                  <SearchableSelect
                    id="schedule-customer-select"
                    placeholder="-- Choose Customer --"
                    options={partiesList || []}
                    value={newVisitName}
                    isFormStyle={true}
                    getLabel={(p) => p.PartyName}
                    getValue={(p) => p.PartyName}
                    onChange={(val) => setNewVisitName(val)}
                  />
                </div>
              </div>

              {/* Field 3 & 4: Date & Time in Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="schedule-form-group">
                  <label className="schedule-form-label">
                    VISIT DATE *
                  </label>
                  <div className="schedule-input-wrapper">
                    <Calendar className="schedule-input-icon" size={16} />
                    <input
                      type="date"
                      className="schedule-form-input"
                      value={newVisitDate}
                      onChange={(e) => setNewVisitDate(e.target.value)}
                      style={{ colorScheme: 'dark' }}
                      required
                    />
                  </div>
                </div>

                <div className="schedule-form-group">
                  <label className="schedule-form-label">
                    VISIT TIME *
                  </label>
                  <div className="schedule-input-wrapper">
                    <Clock className="schedule-input-icon" size={16} />
                    <input
                      type="time"
                      className="schedule-form-input"
                      value={newVisitTime}
                      onChange={(e) => setNewVisitTime(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Priority select */}
              <div className="schedule-form-group">
                <label className="schedule-form-label">
                  PRIORITY
                </label>
                <div className="schedule-input-wrapper">
                  <select
                    className="schedule-form-input schedule-form-select"
                    value={newVisitPriority}
                    onChange={(e) => setNewVisitPriority(e.target.value)}
                    style={{ backgroundColor: '#121824', borderColor: '#1c2638', color: '#ffffff' }}
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>

              {/* Intelligence Agenda input */}
              <div className="schedule-form-group">
                <label className="schedule-form-label">
                  INTELLIGENCE AGENDA *
                </label>
                <div className="schedule-input-wrapper">
                  <input
                    type="text"
                    className="schedule-form-input"
                    placeholder="e.g., Payment recovery flow..."
                    value={newVisitAgenda}
                    onChange={(e) => setNewVisitAgenda(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button
                  type="button"
                  className="schedule-btn-cancel"
                  onClick={() => setShowScheduleModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="schedule-btn-submit"
                >
                  Schedule Visit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionMan;